import { useCallback, useEffect, useRef, useState } from 'react';
import { formatPhone } from '../../lib/format';
import {
  EngineError,
  addCaseParty,
  addOwnerAsParty,
  markCaseMessagesRead,
  assignCaseMediator,
  CLOSED_STATUS_OPTIONS,
  type ClosedStatusKey,
  closeCase,
  createInvoice,
  getCaseAudit,
  getCaseDetail,
  getEngineSession,
  getMediatorRoster,
  getTimeline,
  recordPartyA2mConsent,
  reopenCase,
  releaseDraftAction,
  sendAction,
  sendMagicLink,
  setCaseName,
  confirmService,
  fetchCaseFileBlob,
  findPartyContact,
  sendCoordinationNote,
  type BusinessContactCandidate,
  listCaseFiles,
  logPartyCall,
  setCaseNumber,
  setClerkArchived,
  setTrialDate,
  startInstantSession,
  updateCaseParty,
  deleteCaseParty,
  ensureSessionMeeting,
  voidInvoice,
  refundInvoice,
  type CaseFile,
  sendPartyA2m,
  sendPartySchedulingDetails,
  escalateToMediation,
  callParty,
  startPhoneVerification,
  verifyPhone,
  getCaseOutreach,
  type PartyOutreach,
} from '../../api/engineDashboard';
import { confirmDialog } from '../../lib/confirmDialog';
import { deriveCaseInsights, type CaseInsights } from '../../lib/caseInsights';
import { deriveCaseStage, stageHintsFromAudit } from '../../lib/caseStage';
import { StageTracker } from '../../components/dashboard/StageTracker';
import { caseQueueLabel, partyRoleLabel, caseStatusLabel, daysUntil, formatDateTime, orderPartiesForDisplay } from '../../lib/labels';
import { formatCurrency, formatDate } from '../../lib/format';
import { EvidenceList } from '../../components/EvidenceList';
import { ArchiveButton } from '../../components/ArchiveButton';
import { NegotiationHistory } from '../../components/NegotiationHistory';
import { PolicyConstraints } from '../../components/PolicyConstraints';
import { SituationSection } from '../../components/SituationSection';
import { SurfacedFocus } from '../../components/SurfacedFocus';
import { Composer } from '../../components/dashboard/Composer';
import { DocumentsPanel } from '../../components/dashboard/DocumentsPanel';
import { FilesPanel } from '../../components/dashboard/FilesPanel';
import { SchedulingPanel } from '../../components/dashboard/SchedulingPanel';
import { NotesPanel } from '../../components/dashboard/NotesPanel';
import { ClerkOutreachPanel } from '../../components/dashboard/ClerkOutreachPanel';
import { InternalMessagingPanel } from '../../components/dashboard/InternalMessagingPanel';
import { TimelinePanel } from '../../components/dashboard/TimelinePanel';
import type { Caps } from './DashboardApp';
import type { Case, Party } from '../../types/case';
import type { AuditEntry, PartyInvoice, TimelineResponse } from '../../types/dashboard';

/** GET /cases/:id also returns invoices[] and trial_date (Engine ≥ Task 6.5). */
type CaseDetail = Case & {
  invoices?: PartyInvoice[];
  trial_date?: string | null;
  scheduling_defaults?: { consultation_minutes: number; mediation_minutes: number };
};

interface Props {
  token: string;
  caseId: string;
  caps: Caps;
  inZoomClient: boolean;
  onBack: () => void;
  onOpenStipEditor?: (agreementId: string, caseTitle?: string | null) => void;
  onUnauthorized: () => void;
}

export function CaseViewPage({ token, caseId, caps, inZoomClient, onBack, onOpenStipEditor, onUnauthorized }: Props) {
  const [detail, setDetail] = useState<CaseDetail | null>(null);
  // Per-party outreach counts/attempts, merged into the Parties section
  // (Rachel, 2026-09-14: the standalone outreach block duplicated the
  // party cards). Metadata only — same endpoint the clerk panel uses.
  const [outreach, setOutreach] = useState<Record<string, PartyOutreach>>({});
  const refreshOutreach = useCallback(() => {
    if (caps.isClerk) return;
    getCaseOutreach(token, caseId)
      .then((res) => {
        const map: Record<string, PartyOutreach> = {};
        for (const po of res.parties) map[po.party_id] = po;
        setOutreach(map);
      })
      .catch(() => {});
  }, [token, caseId, caps.isClerk]);
  useEffect(refreshOutreach, [refreshOutreach]);
  const [caseArchived, setCaseArchived] = useState(false);
  const [sessionStart, setSessionStart] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<TimelineResponse | null>(null);
  const [auditEntries, setAuditEntries] = useState<AuditEntry[] | null>(null);
  // Long audit trails page at 100 server-side (26C1445 hit 236 rows,
  // Danny 2026-09-25) — track the total + cursor so Show-all can walk
  // every page.
  const [auditPage, setAuditPage] = useState<{ total: number | null; cursor: string | null; loadingAll: boolean }>({ total: null, cursor: null, loadingAll: false });
  const [insights, setInsights] = useState<CaseInsights | null>(null);
  const [joinUrl, setJoinUrl] = useState<string | null>(null);
  const [phoneJoin, setPhoneJoin] = useState<{ number: string; passcode: string | null; meeting_id: string } | null>(null);
  const [showAudit, setShowAudit] = useState(false);
  const [portalSending, setPortalSending] = useState(false);
  const [portalNote, setPortalNote] = useState<string | null>(null);
  const [editingPartyId, setEditingPartyId] = useState<string | null>(null);
  // Accordion party rows (Danny, 2026-09-21, third iteration): stacked
  // rows in display order, each independently expandable.
  const [expandedParties, setExpandedParties] = useState<Set<string>>(new Set());
  const [showAddParty, setShowAddParty] = useState(false);
  const [composerPreselect, setComposerPreselect] = useState<{ partyId: string; channel: 'sms' | 'email'; nonce: number } | null>(null);
  const conversationRef = useRef<HTMLDivElement | null>(null);
  const [headerEdit, setHeaderEdit] = useState(false);
  // "Message" from the clerk list lands here — scroll straight to the
  // Messages panel instead of a popup (Danny, 2026-08-28).
  const clerkMessagesRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!detail) return;
    if (sessionStorage.getItem('lh-scroll-messages') !== caseId) return;
    sessionStorage.removeItem('lh-scroll-messages');
    const t = window.setTimeout(() => clerkMessagesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail === null, caseId]);
  // Scheduling rows in the Current overview land here the same way
  // (2026-11-04) — straight to the Scheduling panel.
  const schedulingRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!detail) return;
    if (sessionStorage.getItem('lh-scroll-scheduling') !== caseId) return;
    sessionStorage.removeItem('lh-scroll-scheduling');
    const t = window.setTimeout(() => schedulingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail === null, caseId]);

  const sendPortalLink = async () => {
    if (!window.confirm('Send the tenant their portal link now?')) return;
    setPortalSending(true);
    setPortalNote(null);
    try {
      const res = await sendMagicLink(token, caseId);
      setPortalNote(`Queued via ${res.channel} to ${res.to} — watch the timeline for delivery.`);
    } catch (err) {
      setPortalNote(err instanceof EngineError ? err.message : 'Could not send the portal link.');
    } finally {
      setPortalSending(false);
    }
  };
  // Campus one-button escalation (Rob's CU flow): invite the landlord to
  // agree, invoice both parties $70, and queue the 2-hour mediation.
  const [escalating, setEscalating] = useState(false);
  const [escalateNote, setEscalateNote] = useState<string | null>(null);
  const escalateCampus = async () => {
    const ok = await confirmDialog(
      'This invites the landlord to agree to mediate, invoices BOTH parties $70, and queues the 2-hour mediation. The session books only once both parties have paid.',
      { title: 'Schedule mediation & collect $70 each?', confirmLabel: 'Escalate', danger: true },
    );
    if (!ok) return;
    setEscalating(true);
    setEscalateNote(null);
    try {
      const res = await escalateToMediation(token, caseId);
      setEscalateNote(
        `Landlord${res.landlord ? ` (${res.landlord})` : ''} invited to agree; ${res.invoiced ?? 0} invoice${res.invoiced === 1 ? '' : 's'} sent ($70 each). The mediation books once both have paid.`,
      );
      refreshAudit();
    } catch (err) {
      if (handle401(err)) return;
      setEscalateNote(err instanceof EngineError ? err.message : 'Could not escalate this case.');
    } finally {
      setEscalating(false);
    }
  };
  const [releaseState, setReleaseState] = useState<Record<string, 'working' | 'done' | string>>({});

  const handle401 = useCallback(
    (err: unknown): boolean => {
      if (err instanceof EngineError && err.status === 401) {
        onUnauthorized();
        return true;
      }
      return false;
    },
    [onUnauthorized],
  );

  const refreshTimeline = useCallback(() => {
    getTimeline(token, caseId)
      .then(setTimeline)
      .catch((err: unknown) => {
        if (handle401(err)) return;
        /* 403 = thread content not visible to this principal — panel hides */
      });
  }, [token, caseId, handle401]);

  const refreshAudit = useCallback(() => {
    getCaseAudit(token, caseId)
      .then((res) => {
        setAuditEntries(res.data);
        setAuditPage({ total: res.pagination?.total ?? res.data.length, cursor: res.pagination?.cursor ?? null, loadingAll: false });
        const derived = deriveCaseInsights(res.data);
        setInsights(derived);
        if (derived.bookedSessionId) {
          getEngineSession(token, derived.bookedSessionId)
            .then((session) => {
              // Belt: a cancelled/ended session must never populate the
              // Next-session panel even if the audit mining let it through.
              if (session.status === 'ended' || (session.metadata ?? {})['cancelled'] === true) return;
              const url = (session.metadata ?? {})['join_url'];
              if (typeof url === 'string' && url) setJoinUrl(url);
              if (session.scheduled_start) setSessionStart(session.scheduled_start as string);
              const pj = (session.metadata ?? {})['phone_join'] as
                | { number: string; passcode: string | null; meeting_id: string }
                | undefined;
              if (pj && pj.number) setPhoneJoin(pj);
            })
            .catch(() => {
              /* session lookup not open to JWT sessions yet — no Join button */
            });
        }
      })
      .catch((err: unknown) => {
        if (handle401(err)) return;
      });
  }, [token, caseId, handle401]);

  // Re-fetch the case detail in place (e.g. after lifting the service-of-
  // process hold, so the warning banner clears without a full reload).
  const reloadDetail = useCallback(() => {
    getCaseDetail(token, caseId)
      .then((c) => {
        setDetail(c as CaseDetail);
        setCaseArchived(((c.metadata ?? {}) as Record<string, unknown>).clerk_archived === true);
      })
      .catch((err: unknown) => {
        if (handle401(err)) return;
      });
  }, [token, caseId, handle401]);

  // "Mark read" from the conversation: take this case off the "Unread
  // messages" (awaiting-reply) list without replying (Danny, 2026-10-22). A
  // newer inbound re-raises it. Resets when you switch cases.
  const [convMarkedRead, setConvMarkedRead] = useState(false);
  useEffect(() => setConvMarkedRead(false), [caseId]);
  const markConversationRead = useCallback(async () => {
    setConvMarkedRead(true); // optimistic
    try {
      await markCaseMessagesRead(token, caseId);
    } catch (err: unknown) {
      if (handle401(err)) return;
      setConvMarkedRead(false);
      setError(err instanceof Error ? err.message : 'Could not mark the conversation read');
    }
  }, [token, caseId, handle401]);

  // "Owner signs" toggle: promote the owner on file into a signing party, or
  // remove them again (Danny, 2026-10-22). Reuses the party add/delete paths;
  // delete refuses once they've signed, surfaced as an error.
  const [ownerToggleBusy, setOwnerToggleBusy] = useState(false);
  const toggleOwnerSigner = useCallback(
    async (currentPartyId: string | null) => {
      setOwnerToggleBusy(true);
      setError(null);
      try {
        if (currentPartyId) await deleteCaseParty(token, caseId, currentPartyId);
        else await addOwnerAsParty(token, caseId);
        reloadDetail();
      } catch (err: unknown) {
        if (handle401(err)) return;
        setError(err instanceof Error ? err.message : 'Could not update the owner signer');
      } finally {
        setOwnerToggleBusy(false);
      }
    },
    [token, caseId, reloadDetail, handle401],
  );

  useEffect(() => {
    let cancelled = false;
    getCaseDetail(token, caseId)
      .then((c) => {
        if (!cancelled) {
          setDetail(c as CaseDetail);
          setCaseArchived(((c.metadata ?? {}) as Record<string, unknown>).clerk_archived === true);
        }
      })
      .catch((err: unknown) => {
        if (handle401(err)) return;
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load the case');
      });
    if (!caps.isClerk) refreshTimeline();
    if (!caps.isClerk) refreshAudit();
    return () => {
      cancelled = true;
    };
  }, [token, caseId, caps.isClerk, refreshTimeline, refreshAudit, handle401]);

  // Conversation auto-refresh (Danny, 2026-08-22): poll every 20s while
  // the tab is visible, and refresh immediately when the tab regains
  // focus. The manual Refresh button stays for impatience.
  useEffect(() => {
    if (caps.isClerk) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') refreshTimeline();
    }, 20_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshTimeline();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [caps.isClerk, refreshTimeline]);

  const releaseDraft = async (actionId: string) => {
    setReleaseState((prev) => ({ ...prev, [actionId]: 'working' }));
    try {
      await releaseDraftAction(token, actionId);
      setReleaseState((prev) => ({ ...prev, [actionId]: 'done' }));
      refreshAudit();
      refreshTimeline();
    } catch (err) {
      if (handle401(err)) return;
      setReleaseState((prev) => ({
        ...prev,
        [actionId]: err instanceof Error ? err.message : 'Release failed',
      }));
    }
  };

  if (error) {
    return (
      <div className="card p-6 text-center">
        <p className="text-sm text-red-600">{error}</p>
        <button type="button" className="btn-secondary mt-3" onClick={onBack}>
          Back to the queue
        </button>
      </div>
    );
  }

  if (!detail) {
    return <div className="p-8 text-center text-sm text-gray-500">Loading case…</div>;
  }

  const trialDays = detail.trial_date ? daysUntil(detail.trial_date) : null;

  const invoices = detail.invoices ?? [];
  const pendingDrafts = insights?.pendingDrafts ?? [];
  const stage = deriveCaseStage(detail.status, stageHintsFromAudit(auditEntries ?? []));
  const a2mConsents = ((detail.metadata ?? {}) as { a2m_consents?: Record<string, string> }).a2m_consents ?? {};
  // Service-of-process hold (court cases): the engine refuses to contact
  // the Defendant until the Plaintiff confirms service. Active when
  // service_status is 'pending' or 'unknown' (undefined / 'confirmed' = no hold).
  const serviceStatus = ((detail.metadata ?? {}) as Record<string, unknown>).service_status as
    | 'confirmed'
    | 'pending'
    | 'unknown'
    | undefined;
  const onServiceHold =
    detail.domain_pack_id === 'court_dispute' && (serviceStatus === 'pending' || serviceStatus === 'unknown');

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-3">
        <button type="button" className="btn-secondary !py-1 !text-xs" onClick={onBack}>
          ← Queue
        </button>
        <div className="min-w-0 flex-1">
          {/* Mirrors the queue row: partner chip, case# · caption, then
              stage/priority/trial and the party contact lines. */}
          {headerEdit ? (
            <HeaderEditRow
              token={token}
              caseId={caseId}
              isCourt={detail.domain_pack_id === 'court_dispute'}
              caseNo={((detail.metadata ?? {}) as Record<string, unknown>).court_case_no as string | null ?? null}
              caseName={((detail.metadata ?? {}) as Record<string, unknown>).case_name as string | null ?? null}
              trialDate={detail.trial_date ?? null}
              onDone={(patch) => {
                setHeaderEdit(false);
                if (patch) {
                  setDetail((prev) =>
                    prev
                      ? {
                          ...prev,
                          trial_date: patch.trial_date !== undefined ? patch.trial_date : prev.trial_date,
                          metadata: {
                            ...(prev.metadata ?? {}),
                            ...(patch.case_name !== undefined ? { case_name: patch.case_name ?? undefined } : {}),
                            ...(patch.court_case_no !== undefined ? { court_case_no: patch.court_case_no } : {}),
                            ...(patch.division ? { division: patch.division } : {}),
                          },
                        }
                      : prev,
                  );
                }
              }}
            />
          ) : (
            <>
              <div className="flex items-center gap-2">
                <span className="shrink-0 rounded bg-surface-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500">
                  {detail.org_name ?? 'Levelheaded'}
                </span>
                <h1 className="truncate text-lg font-semibold">
                  {(() => {
                    const l = caseQueueLabel({
                      metadata: detail.metadata,
                      claims: detail.claims,
                      org_name: detail.org_name,
                      org_source_type: detail.org_source_type,
                      property_name: detail.property?.name ?? null,
                      parties: detail.parties,
                    });
                    return l.caseNo ? `${l.caseNo} · ${l.name}` : l.name;
                  })()}
                </h1>
                {caps.canMutate && !caps.isClerk && (
                  <button
                    type="button"
                    className="shrink-0 text-xs text-gray-400 hover:text-brand"
                    title="Edit case number, case name, and court date"
                    onClick={() => setHeaderEdit(true)}
                  >
                    ✎ Edit
                  </button>
                )}
                <CopyCaseLink caseId={caseId} />
              </div>
              <div className="text-xs text-gray-500">
                {caseStatusLabel(detail.status)} · {detail.priority} priority
                {detail.trial_date && (
                  <span className={trialDays !== null && trialDays <= 7 ? 'text-red-600' : ''}>
                    {' '}
                    · Trial {formatDate(detail.trial_date)}
                    {trialDays !== null && trialDays >= 0 ? ` (${trialDays} days)` : ''}
                  </span>
                )}
              </div>
              {/* How parties reach this case — the inbound email they reply to
                  and the SMS number texts come from (Danny, 2026-10-22). */}
              {!caps.isClerk && (detail.channels?.inbound_email || detail.channels?.sms_number) && (
                <div className="mt-0.5 text-xs text-gray-500">
                  Parties reach this case at
                  {detail.channels.inbound_email && (
                    <> <span className="font-medium text-gray-700">{detail.channels.inbound_email}</span></>
                  )}
                  {detail.channels.inbound_email && detail.channels.sms_number && ' ·'}
                  {detail.channels.sms_number && (
                    <> text <span className="font-medium text-gray-700">{formatPhone(detail.channels.sms_number)}</span></>
                  )}
                  {detail.channels.inbound_email && (
                    <div className="text-[11px] text-gray-400">
                      Emails must come from an address on the case to route automatically.
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </header>

      {onServiceHold && (
        <ServiceHoldBanner
          token={token}
          caseId={caseId}
          canMutate={caps.canMutate}
          onConfirmed={reloadDetail}
          onUnauthorized={onUnauthorized}
        />
      )}

      <StageTracker stage={stage} />

      <div className="grid gap-4 lg:grid-cols-3">
        {/* ── Main column ─────────────────────────────────── */}
        <div className="space-y-4 lg:col-span-2">
          {/* Clerk outreach panel stays THEIR primary parties view; staff
              see the same data merged into the Parties section below
              (Rachel, 2026-09-14: two redundant party blocks condensed). */}
          {caps.isClerk && (
            <ClerkOutreachPanel
              token={token}
              caseId={caseId}
              isCourt={detail.domain_pack_id === 'court_dispute'}
              readOnly={caps.readOnly}
              onUnauthorized={onUnauthorized}
            />
          )}
          {caps.isClerk && (
            <>
            <ClerkSharedFiles token={token} caseId={caseId} />
            <div ref={clerkMessagesRef}>
              <InternalMessagingPanel token={token} caseId={caseId} onUnauthorized={onUnauthorized} />
            </div>
            </>
          )}

          <section className="card">
            {/* Party accordion (Danny, 2026-09-21): every party is its own
                stacked row in display order (plaintiffs, defendants,
                others); clicking a row expands it to the full detail
                panel. Rows expand/collapse independently. */}
            {(() => {
              const ordered = orderPartiesForDisplay(detail.parties);
              const mainParties = ordered.filter((p) => ['claimant', 'respondent'].includes(p.role_archetype));
              // Property owner / PM contact, shown under Parties for PM/campus
              // cases (Rachel/Danny, 2026-10-09). Imported Nomad cases carry
              // the real owner in the property directory; publicly-filed cases
              // have no property record, so we fall back to the owner-side
              // party (the property_manager/owner/landlord, else the claimant).
              const isPmLike =
                (detail.org_source_type === 'pm_firm' || detail.org_source_type === 'campus') &&
                detail.domain_pack_id !== 'community_dispute';
              const ownerSideParty = isPmLike
                ? ordered.find((p) => ['property_manager', 'owner', 'landlord'].includes((p.role ?? '').toLowerCase()))
                  ?? ordered.find((p) => p.role_archetype === 'claimant')
                : undefined;
              const ownerContact = detail.property?.owner
                ? { name: detail.property.owner.name ?? 'On file', email: detail.property.owner.email ?? null, phone: detail.property.owner.phone ?? null, fromDirectory: true }
                : isPmLike && ownerSideParty
                  ? { name: ownerSideParty.name, email: ownerSideParty.email ?? null, phone: ownerSideParty.phone ?? null, fromDirectory: false }
                  : null;
              const agreedCount = mainParties.filter((p) => a2mConsents[p.id]).length;
              const roleWord = (p: (typeof ordered)[number]) =>
                partyRoleLabel(p, detail.domain_pack_id === 'court_dispute' ? 'court' : null);
              const dotClass = (p: (typeof ordered)[number]) =>
                !['claimant', 'respondent'].includes(p.role_archetype) || ['RESOLVED', 'CLOSED'].includes(detail.status)
                  ? 'bg-gray-300'
                  : a2mConsents[p.id]
                    ? 'bg-teal-500'
                    : 'bg-amber-400';
              const toggleParty = (id: string) =>
                setExpandedParties((prev) => {
                  const next = new Set(prev);
                  if (next.has(id)) {
                    next.delete(id);
                    setEditingPartyId((e) => (e === id ? null : e));
                  } else next.add(id);
                  return next;
                });
              return (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-y-1 border-b border-surface-border px-3 py-2">
                    <span className="section-label">Parties</span>
                    <div className="flex shrink-0 items-center gap-3">
                      {mainParties.length > 0 && (
                        <span className="text-xs text-gray-500">{agreedCount} of {mainParties.length} agreed to mediate</span>
                      )}
                      {caps.canMutate && (
                        <button type="button" className="text-xs font-medium text-brand hover:underline" onClick={() => setShowAddParty((v) => !v)}>
                          + Add party
                        </button>
                      )}
                    </div>
                  </div>
                  {ownerContact && (
                    <div className="border-b border-surface-border bg-surface-muted/40 px-3 py-2 text-sm">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Property owner / PM contact</div>
                      <div className="mt-0.5 font-medium text-gray-900">{ownerContact.name}</div>
                      <div className="text-xs text-gray-600">
                        {[ownerContact.email, ownerContact.phone].filter(Boolean).join(' · ') || 'no contact on file'}
                      </div>
                      {ownerContact.fromDirectory && (
                        <div className="mt-0.5 text-[11px] text-gray-400">
                          From the owner directory. Whether the PM or the owner signs is per-property — confirm with the org before requesting signatures.
                        </div>
                      )}
                      {/* Owner-signs toggle: promote the owner on file into a
                          signing party (or remove), so the mediator doesn't
                          re-key them when the owner is a signatory. */}
                      {ownerContact.fromDirectory && caps.canMutate && !['RESOLVED', 'CLOSED'].includes(detail.status) && (
                        detail.property?.owner?.party_id ? (
                          <div className="mt-1.5 flex items-center gap-2 text-[11px]">
                            <span className="inline-flex items-center gap-1 font-medium text-teal-700">✓ Added as a signing party</span>
                            <button
                              type="button"
                              className="font-medium text-red-600 hover:underline disabled:opacity-50"
                              disabled={ownerToggleBusy}
                              onClick={() => void toggleOwnerSigner(detail.property!.owner!.party_id ?? null)}
                            >
                              {ownerToggleBusy ? 'Working…' : 'Remove'}
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="mt-1.5 text-xs font-medium text-brand hover:underline disabled:opacity-50"
                            disabled={ownerToggleBusy}
                            onClick={() => void toggleOwnerSigner(null)}
                          >
                            {ownerToggleBusy ? 'Working…' : '+ Add owner as a signing party'}
                          </button>
                        )
                      )}
                    </div>
                  )}
                  {showAddParty && caps.canMutate && (
                    <div className="border-b border-surface-border p-3">
                      <AddPartyForm
                        token={token}
                        caseId={caseId}
                        onAdded={(party) => {
                          setDetail((prev) => (prev ? { ...prev, parties: [...prev.parties, party] } : prev));
                          setShowAddParty(false);
                          setExpandedParties((prev) => new Set(prev).add(party.id));
                        }}
                        onUnauthorized={onUnauthorized}
                      />
                    </div>
                  )}
                  {ordered.map((p) => {
                    const expanded = expandedParties.has(p.id);
                    const consentAt = a2mConsents[p.id] ?? null;
                    const showA2m =
                      ['claimant', 'respondent'].includes(p.role_archetype) && !['RESOLVED', 'CLOSED'].includes(detail.status);
                    const o = outreach[p.id];
                    const lastAttemptAt = o?.attempts?.length ? o.attempts.map((a) => a.at).sort().slice(-1)[0] : null;
                    // Payment at a glance (Danny, 2026-10-18): surface Paid/Not paid
                    // on the collapsed row so you don't open details to check. Only
                    // when this party actually has an invoice (paid, or owed = sent/draft).
                    const partyInvoices = invoices.filter((i) => i.party_id === p.id);
                    const invoicePaid = partyInvoices.some((i) => i.status === 'paid');
                    const invoiceOwed = !invoicePaid && partyInvoices.some((i) => i.status === 'sent' || i.status === 'draft');
                    return (
                      <div key={p.id} className="border-b border-surface-border last:border-b-0">
                        <button
                          type="button"
                          aria-expanded={expanded}
                          onClick={() => toggleParty(p.id)}
                          className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-surface-muted/60"
                        >
                          <span className={`h-2 w-2 shrink-0 rounded-full ${dotClass(p)}`} />
                          <span className="truncate text-sm font-semibold text-gray-900">{p.name}</span>
                          <span className="shrink-0 text-xs text-gray-400">{roleWord(p)}</span>
                          <span className="ml-auto flex shrink-0 items-center gap-2">
                            {showA2m && (
                              consentAt
                                ? <span className="text-[11px] font-medium text-teal-700">✓ agreed</span>
                                : <span className="text-[11px] font-medium text-amber-700">awaiting A2M</span>
                            )}
                            {invoicePaid && (
                              <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-800">Paid ✓</span>
                            )}
                            {invoiceOwed && (
                              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">Not paid</span>
                            )}
                            {!p.phone && !p.email && (
                              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">no contact info</span>
                            )}
                            <span aria-hidden className="text-xs text-gray-400">{expanded ? '▾' : '▸'}</span>
                          </span>
                        </button>
                        {expanded && (editingPartyId === p.id ? (
                          <div className="p-3 pt-0">
                            <EditPartyForm
                              token={token}
                              caseId={caseId}
                              party={p}
                              siblings={detail.parties.filter((x) => x.id !== p.id)}
                              domainPack={detail.domain_pack_id}
                              onSaved={(updated) => {
                                setDetail((prev) =>
                                  prev ? { ...prev, parties: prev.parties.map((x) => (x.id === updated.id ? updated : x)) } : prev,
                                );
                                setEditingPartyId(null);
                              }}
                              onCancel={() => setEditingPartyId(null)}
                              onDeleted={() => {
                                setDetail((prev) =>
                                  prev ? { ...prev, parties: prev.parties.filter((x) => x.id !== p.id) } : prev,
                                );
                                setEditingPartyId(null);
                              }}
                              onUnauthorized={onUnauthorized}
                            />
                          </div>
                        ) : (
                          <>
                            <div className="grid gap-4 px-3 pb-3 sm:grid-cols-3">
                              <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Contact</div>
                                <div className="mt-1.5 space-y-1 text-sm text-gray-700">
                                  {p.phone && (
                                    <div className="flex items-center gap-1.5">
                                      <span aria-hidden>💬</span> {formatPhone(p.phone)}
                                      {p.communication_preference === 'sms' && (
                                        <span className="rounded-full bg-teal-50 px-1.5 py-0.5 text-[10px] font-medium text-teal-700">Preferred</span>
                                      )}
                                    </div>
                                  )}
                                  {p.email && (
                                    <div className="flex items-center gap-1.5">
                                      <span aria-hidden>✉️</span> <span className="break-all">{p.email}</span>
                                      {p.communication_preference === 'email' && (
                                        <span className="rounded-full bg-teal-50 px-1.5 py-0.5 text-[10px] font-medium text-teal-700">Preferred</span>
                                      )}
                                    </div>
                                  )}
                                  {!p.phone && !p.email && (
                                    <div className="text-xs text-gray-400">
                                      No contact info on file.
                                      {caps.canMutate && (
                                        <span className="ml-1">
                                          <FindContactControl
                                            token={token}
                                            caseId={caseId}
                                            party={p}
                                            onApplied={(updated) => {
                                              setDetail((prev) =>
                                                prev ? { ...prev, parties: prev.parties.map((x) => (x.id === updated.id ? updated : x)) } : prev,
                                              );
                                            }}
                                            onSearched={refreshAudit}
                                            onUnauthorized={onUnauthorized}
                                          />
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {typeof p.metadata?.business_name === 'string' && p.metadata.business_name ? (
                                    <>
                                      <div className="text-xs text-gray-500">🏢 {String(p.metadata.business_name)}</div>
                                      <CoordinationNoteControl
                                        token={token}
                                        caseId={caseId}
                                        party={p}
                                        canMutate={caps.canMutate}
                                        onSent={reloadDetail}
                                        onUnauthorized={onUnauthorized}
                                      />
                                    </>
                                  ) : null}
                                  <PartyContactExtras
                                    token={token}
                                    caseId={caseId}
                                    party={p}
                                    canMutate={caps.canMutate}
                                    onUpdated={(updated) =>
                                      setDetail((prev) =>
                                        prev ? { ...prev, parties: prev.parties.map((x) => (x.id === updated.id ? updated : x)) } : prev,
                                      )
                                    }
                                    onUnauthorized={onUnauthorized}
                                  />
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Status</div>
                                <div className="mt-1.5 text-sm">
                                  {showA2m ? (
                                    consentAt ? (
                                      <>
                                        <div className="flex items-center gap-1 font-medium text-teal-700">✓ Agreed to mediate</div>
                                        <div className="mt-0.5 text-xs text-gray-500">Confirmed {new Date(consentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                                      </>
                                    ) : (
                                      <>
                                        <div className="text-amber-700">Awaiting Agreement to Mediate</div>
                                        {caps.canMutate && (
                                          <div className="mt-1 flex flex-wrap gap-2">
                                            {(p.email || p.phone) && (
                                              <button
                                                type="button"
                                                className="rounded bg-brand px-2 py-0.5 text-[11px] font-medium text-white hover:opacity-90"
                                                onClick={() => {
                                                  if (!window.confirm(`Send ${p.name} the Agreement to Mediate terms by ${[p.phone && 'text', p.email && 'email'].filter(Boolean).join(' and ')}?\n\nThey review the terms at a private link; pressing "I agree" records their consent automatically.`)) return;
                                                  sendPartyA2m(token, caseId, p.id)
                                                    .then((res) => window.alert(res.queued > 0 ? `Sent (${res.queued} message${res.queued === 1 ? '' : 's'}).` : 'Already sent today on every available channel.'))
                                                    .catch((err) => window.alert(err instanceof EngineError ? err.message : 'Could not send the agreement.'));
                                                }}
                                              >
                                                Send Agreement to Mediate
                                              </button>
                                            )}
                                            <button
                                              type="button"
                                              className="text-[11px] text-gray-400 underline decoration-dotted hover:text-brand hover:decoration-solid"
                                              onClick={() => {
                                                if (!window.confirm(`Record that ${p.name} agreed to mediate (verbally or offline)?\n\nThis satisfies the Agreement to Mediate requirement and is logged in the audit trail under your name.`)) return;
                                                recordPartyA2mConsent(token, caseId, p.id, 'verbal')
                                                  .then((res) =>
                                                    setDetail((prev) =>
                                                      prev
                                                        ? { ...prev, metadata: { ...(prev.metadata ?? {}), a2m_consents: { ...a2mConsents, [p.id]: res.consented_at } } }
                                                        : prev,
                                                    ),
                                                  )
                                                  .catch((err) => window.alert(err instanceof EngineError ? err.message : 'Could not record the agreement.'));
                                              }}
                                            >
                                              record verbal (last resort)…
                                            </button>
                                          </div>
                                        )}
                                      </>
                                    )
                                  ) : (
                                    <div className="text-xs text-gray-400">{['claimant', 'respondent'].includes(p.role_archetype) ? 'Case closed out' : (typeof p.metadata?.relationship === 'string' ? String(p.metadata.relationship) : 'Additional participant')}</div>
                                  )}
                                </div>
                              </div>
                              {caps.canMutate && (p.email || p.phone) && (
                                <div>
                                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Scheduling</div>
                                  <div className="mt-1.5">
                                    <button
                                      type="button"
                                      className="rounded border border-brand px-2 py-0.5 text-[11px] font-medium text-brand hover:bg-brand hover:text-white"
                                      title="Send this person the session time, Zoom link, phone dial-in, and calendar invite"
                                      onClick={() => {
                                        if (!window.confirm(`Send ${p.name} the scheduling details (session time, Zoom link, phone dial-in, calendar invite) by ${[p.phone && 'text', p.email && 'email'].filter(Boolean).join(' and ')}?`)) return;
                                        sendPartySchedulingDetails(token, caseId, p.id)
                                          .then((res) =>
                                            window.alert(
                                              `Sent to ${p.name} (${(res.channels ?? []).join(' + ') || 'no channel'}).${res.session_time ? `\n\nSession: ${res.session_time}` : ''}`,
                                            ),
                                          )
                                          .catch((err) =>
                                            window.alert(err instanceof EngineError ? err.message : 'Could not send scheduling details.'),
                                          );
                                      }}
                                    >
                                      📅 Send scheduling details
                                    </button>
                                  </div>
                                </div>
                              )}
                              <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Outreach</div>
                                <div className="mt-1.5 text-sm text-gray-700">
                                  {o ? (
                                    <>
                                      <div>{o.counts.email} email{o.counts.email === 1 ? '' : 's'} · {o.counts.sms} text{o.counts.sms === 1 ? '' : 's'} · {o.counts.call} call{o.counts.call === 1 ? '' : 's'}</div>
                                      {lastAttemptAt && (
                                        <div className="mt-0.5 text-xs text-gray-500">
                                          Last contact {new Date(lastAttemptAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                        </div>
                                      )}
                                      {o.attempts.length > 0 && <AttemptsToggle attempts={o.attempts} />}
                                    </>
                                  ) : (
                                    <div className="text-xs text-gray-400">No outreach yet.</div>
                                  )}
                                </div>
                              </div>
                            </div>
                            {!caps.isClerk && (
                              <PartyInvoiceBlock
                                token={token}
                                caseId={caseId}
                                party={p}
                                invoices={invoices}
                                canMutate={caps.canMutate}
                                canRefund={caps.role === 'admin' || caps.isAdminMediator}
                                onPartyUpdated={(updated) =>
                                  setDetail((prev) =>
                                    prev ? { ...prev, parties: prev.parties.map((x) => (x.id === updated.id ? updated : x)) } : prev,
                                  )
                                }
                                onInvoicesChanged={(mut) =>
                                  setDetail((prev) => (prev ? { ...prev, invoices: mut(prev.invoices ?? []) } : prev))
                                }
                                onUnauthorized={onUnauthorized}
                              />
                            )}
                            <div className="flex flex-wrap items-center gap-2 border-t border-surface-border px-3 py-2">
                              {caps.canMutate && p.phone && (
                                <button
                                  type="button"
                                  className="btn-secondary !py-1 !text-xs"
                                  onClick={() => {
                                    setComposerPreselect({ partyId: p.id, channel: 'sms', nonce: Date.now() });
                                    conversationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }}
                                >
                                  Text
                                </button>
                              )}
                              {caps.canMutate && p.email && (
                                <button
                                  type="button"
                                  className="btn-secondary !py-1 !text-xs"
                                  onClick={() => {
                                    setComposerPreselect({ partyId: p.id, channel: 'email', nonce: Date.now() });
                                    conversationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }}
                                >
                                  Email
                                </button>
                              )}
                              {caps.canMutate && p.phone && (
                                <CallControl token={token} caseId={caseId} party={p} />
                              )}
                              {caps.canMutate && (
                                <LogCallControl token={token} caseId={caseId} party={p} onLogged={() => { refreshAudit(); refreshOutreach(); }} />
                              )}
                              {caps.canMutate && (
                                <button type="button" className="ml-auto text-xs text-gray-400 hover:text-brand" onClick={() => setEditingPartyId(p.id)}>
                                  Edit details
                                </button>
                              )}
                            </div>
                          </>
                        ))}
                      </div>
                    );
                  })}
                </>
              );
            })()}
          </section>

          {!caps.isClerk && (
            <section className="card" ref={conversationRef}>
              <div className="flex items-center gap-2 border-b border-surface-border p-3">
                <div className="section-label">Conversation</div>
                {caps.canMutate && (
                  <button
                    type="button"
                    className="btn-secondary ml-auto !py-1 !text-xs disabled:opacity-60"
                    disabled={convMarkedRead}
                    title="Take this case off the 'Unread messages' list without replying. It comes back if the party writes again."
                    onClick={() => void markConversationRead()}
                  >
                    {convMarkedRead ? 'Marked read ✓' : 'Mark read'}
                  </button>
                )}
                <button
                  type="button"
                  className={`btn-secondary !py-1 !text-xs${caps.canMutate ? '' : ' ml-auto'}`}
                  onClick={refreshTimeline}
                >
                  Refresh
                </button>
              </div>
              <TimelinePanel timeline={timeline?.timeline ?? []} token={token} />
              {caps.canMutate ? (
                <Composer
                  preselect={composerPreselect}
                  token={token}
                  caseId={caseId}
                  parties={detail.parties}
                  onSent={refreshTimeline}
                  onUnauthorized={onUnauthorized}
                />
              ) : (
                <div className="border-t border-surface-border p-3 text-xs text-gray-500">
                  Your account is read-only — sending is disabled.
                </div>
              )}
            </section>
          )}

          {/* Clerks/judges never see the situation, evidence, or documents
              (Danny, 2026-08-17). The API masks the same data server-side —
              this hiding is presentation, not the boundary. */}
          {!caps.isClerk && <SituationSection case={detail} />}
          {!caps.isClerk && (
            <>
              <SurfacedFocus recommendations={detail.recommendations} />
              <NegotiationHistory recommendations={detail.recommendations} />
              <PolicyConstraints case={detail} />
            </>
          )}
          {!caps.isClerk && <EvidenceList evidence={detail.evidence} token={token} />}

          {!caps.isClerk && (
          <section className="card">
            <button
              type="button"
              className="flex w-full items-center justify-between p-4 text-left"
              onClick={() => setShowAudit((v) => !v)}
            >
              <span className="section-label">Audit trail</span>
              <span className="text-xs text-gray-400">{showAudit ? 'Hide' : 'Show'}</span>
            </button>
            {showAudit && (
              <ol className="max-h-80 space-y-2 overflow-y-auto border-t border-surface-border p-4 text-xs">
                {(auditEntries ?? []).map((e) => (
                  <li key={e.id} className="border-l-2 border-surface-border pl-2">
                    <div className="text-gray-400">
                      {formatDateTime(e.timestamp)} · {e.event_type}
                    </div>
                    <div className="text-gray-700">{e.description}</div>
                  </li>
                ))}
                {(auditEntries ?? []).length === 0 && (
                  <li className="text-gray-500">No audit entries.</li>
                )}
                {auditPage.cursor && (
                  <li className="pt-1">
                    <button
                      type="button"
                      className="text-brand hover:underline"
                      disabled={auditPage.loadingAll}
                      onClick={async () => {
                        setAuditPage((p) => ({ ...p, loadingAll: true }));
                        try {
                          let cursor: string | null = auditPage.cursor;
                          let all = auditEntries ?? [];
                          // Walk every remaining page (100/page).
                          while (cursor) {
                            const res = await getCaseAudit(token, caseId, 100, cursor);
                            all = [...all, ...res.data];
                            cursor = res.pagination?.cursor ?? null;
                          }
                          setAuditEntries(all);
                          setAuditPage({ total: all.length, cursor: null, loadingAll: false });
                        } catch {
                          setAuditPage((p) => ({ ...p, loadingAll: false }));
                        }
                      }}
                    >
                      {auditPage.loadingAll
                        ? 'Loading all entries…'
                        : `Show all ${auditPage.total ?? ''} entries (${(auditEntries ?? []).length} shown)`}
                    </button>
                  </li>
                )}
              </ol>
            )}
          </section>
          )}
        </div>

        {/* ── Right rail ──────────────────────────────────── */}
        <div className="space-y-4">
          {!caps.isClerk && (
            <MediatorAssignPanel
              token={token}
              caseId={caseId}
              orgId={detail.org_id}
              assignedId={(detail as { assigned_mediator_id?: string | null }).assigned_mediator_id ?? null}
              canMutate={caps.canMutate}
            />
          )}
          <section className="card p-4">
            <div className="section-label">Next session</div>
            {(insights?.bookedSlot ?? sessionStart) ? (
              <div className="mt-2 text-sm">
                <div className="font-medium">{formatDateTime(insights?.bookedSlot ?? sessionStart)}</div>
                {joinUrl ? (
                  <a
                    href={joinUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary mt-2 w-full"
                  >
                    Join session
                  </a>
                ) : (
                  <>
                  <p className="mt-1 text-xs text-gray-500">
                    {inZoomClient
                      ? 'Join from your Zoom meetings list.'
                      : 'Join link not available yet.'}
                  </p>
                  {/* Booked-but-link-less session (26S402): the meeting
                      creation failed at booking — mint it now, hosted by
                      the case's current mediator. */}
                  {caps.canMutate && insights?.bookedSessionId && (
                    <button
                      type="button"
                      className="btn-secondary mt-2 w-full !text-xs"
                      onClick={async () => {
                        try {
                          const res = await ensureSessionMeeting(token, caseId, insights.bookedSessionId!);
                          if (res.join_url) setJoinUrl(res.join_url);
                        } catch (err) {
                          if (err instanceof EngineError && err.status === 401) return onUnauthorized();
                          window.alert(err instanceof EngineError ? err.message : 'Could not create the meeting link.');
                        }
                      }}
                    >
                      Generate meeting link
                    </button>
                  )}
                  </>
                )}
                {phoneJoin && (
                  <p className="mt-2 text-xs text-gray-600" title="Read this to a party who can't launch Zoom — they can dial in from any phone">
                    📞 Phone option: {phoneJoin.number} · meeting ID{' '}
                    {phoneJoin.meeting_id.replace(/\D/g, '').replace(/^(\d{3})(\d{4})(\d+)$/, '$1 $2 $3')}
                    {phoneJoin.passcode ? ` · passcode ${phoneJoin.passcode}` : ''}
                  </p>
                )}
              </div>
            ) : (
              <p className="mt-2 text-sm text-gray-500">No session booked.</p>
            )}
            {caps.canMutate && (
              <InstantSessionControl
                token={token}
                caseId={caseId}
                parties={detail.parties}
                onUnauthorized={onUnauthorized}
              />
            )}
          </section>

          {/* The offers/chat portal exists only for the delinquency pack —
              court parties have the intake/invite flow instead. */}
          {caps.canMutate && detail.domain_pack_id === 'rent_delinquency' && (
            <section className="card p-4">
              <div className="section-label">Tenant portal</div>
              <p className="mt-1 text-xs text-gray-500">
                Text/email the tenant their private portal link (offers, chat,
                mediation opt-in) through Levelheaded — delivery shows on the timeline.
              </p>
              <button
                type="button"
                className="btn-secondary mt-2 w-full"
                disabled={portalSending}
                onClick={() => void sendPortalLink()}
              >
                {portalSending ? 'Sending…' : 'Send portal link'}
              </button>
              {portalNote && <p className="mt-1.5 text-xs text-gray-600">{portalNote}</p>}
            </section>
          )}

          {/* Campus (CU) escalation — after the 15-min student consult, one
              button invites the landlord + invoices both + queues the 2-hour
              mediation. Only for campus sources; hidden once mediation is set. */}
          {caps.canMutate && detail.org_source_type === 'campus' && (
            <section className="card p-4">
              <div className="section-label">After the consult</div>
              <p className="mt-1 text-xs text-gray-500">
                Ready to proceed? This invites the landlord to agree to mediate,
                invoices <strong>both parties $70</strong> (paid to Levelheaded),
                and queues the 2-hour mediation — which books only once both have paid.
              </p>
              <button
                type="button"
                className="btn-primary mt-2 w-full"
                disabled={escalating}
                onClick={() => void escalateCampus()}
              >
                {escalating ? 'Working…' : 'Schedule mediation (collect $70 each)'}
              </button>
              {escalateNote && <p className="mt-1.5 text-xs text-gray-600">{escalateNote}</p>}
            </section>
          )}

          {!caps.isClerk && (
            <div ref={schedulingRef}>
              <SchedulingPanel
                token={token}
                caseId={caseId}
                initialTaskId={insights?.schedulingTaskId ?? null}
                canMutate={caps.canMutate}
                schedulingDefaults={detail.scheduling_defaults}
                onUnauthorized={onUnauthorized}
              />
            </div>
          )}

          <DocumentsPanel
            token={token}
            caseId={caseId}
            canMutate={caps.canMutate}
            domainPack={detail.domain_pack_id}
            romSeedParticipants={detail.parties
              .filter((p) => ['claimant', 'respondent'].includes(p.role_archetype))
              .sort((a, b) => (a.role_archetype === 'claimant' ? -1 : 1) - (b.role_archetype === 'claimant' ? -1 : 1))
              .map((p) => ({
                name: p.name,
                role_label: partyRoleLabel(p, detail.domain_pack_id === 'court_dispute' ? 'court' : null),
              }))}
            mediationHeld={
              ['MEDIATING', 'RESOLVED', 'CLOSED'].includes(detail.status) ||
              (insights?.bookedSlot != null && new Date(insights.bookedSlot).getTime() <= Date.now())
            }
            onOpenStipEditor={
              onOpenStipEditor
                ? (agreementId: string) =>
                    onOpenStipEditor(
                      agreementId,
                      (((detail.metadata ?? {}) as Record<string, unknown>).case_name as string | null) ?? null,
                    )
                : undefined
            }
            onUnauthorized={onUnauthorized}
          />

          {!caps.isClerk && (
          <FilesPanel
            isCourt={detail.domain_pack_id === 'court_dispute'}
            token={token}
            caseId={caseId}
            canMutate={caps.canMutate}
            onUnauthorized={onUnauthorized}
          />
          )}

          {!caps.isClerk && (
            <InternalMessagingPanel token={token} caseId={caseId} onUnauthorized={onUnauthorized} />
          )}

          {!caps.isClerk && (
          <NotesPanel
            token={token}
            caseId={caseId}
            canMutate={caps.canMutate}
            onUnauthorized={onUnauthorized}
          />
          )}

          {!caps.isClerk && pendingDrafts.length > 0 && (
            <section className="card p-4">
              <div className="section-label">Drafts awaiting release</div>
              <ul className="mt-2 space-y-2">
                {pendingDrafts.map((draft, i) => {
                  const state = draft.actionId ? releaseState[draft.actionId] : undefined;
                  return (
                    <li key={draft.actionId ?? i} className="text-sm">
                      <p className="text-gray-700">{draft.description}</p>
                      <div className="mt-0.5 text-xs text-gray-400">
                        Drafted {formatDateTime(draft.draftedAt)}
                      </div>
                      {caps.canMutate && draft.actionId && state !== 'done' && (
                        <button
                          type="button"
                          className="btn-primary mt-1 !py-1 !text-xs"
                          disabled={state === 'working'}
                          onClick={() => void releaseDraft(draft.actionId!)}
                        >
                          {state === 'working' ? 'Releasing…' : 'Review complete — release'}
                        </button>
                      )}
                      {state === 'done' && (
                        <div className="mt-1 text-xs font-medium text-green-700">
                          Released to the send queue ✓
                        </div>
                      )}
                      {state && state !== 'working' && state !== 'done' && (
                        <div className="mt-1 text-xs text-red-600">{state}</div>
                      )}
                      {!draft.actionId && (
                        <div className="mt-1 text-xs text-gray-400">
                          Release from the source system (id not recorded).
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

        </div>
      </div>

      {/* Archive — hides the case from queues and the agenda everywhere
          (server-side shared flag, audited, reversible). Close — a real
          status transition to CLOSED with a required why (irreversible;
          imported cases get retired this way). */}
      <div className="mt-4 flex items-center justify-end gap-2 border-t border-surface-border pt-3">
        {caps.canMutate && !caps.isClerk && detail.status === 'CLOSED' && (
          <ReopenCaseControl
            token={token}
            caseId={caseId}
            onReopened={() => setDetail((prev) => (prev ? { ...prev, status: 'EVALUATING' } : prev))}
          />
        )}
        {caps.canMutate && !caps.isClerk && detail.status !== 'CLOSED' && (
          <CloseCaseControl
            token={token}
            caseId={caseId}
            onClosed={() => {
              setDetail((prev) => (prev ? { ...prev, status: 'CLOSED' } : prev));
            }}
          />
        )}
        {caseArchived && (
          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">Archived</span>
        )}
        <ArchiveButton
          isArchived={caseArchived}
          onToggle={() => {
            const next = !caseArchived;
            setCaseArchived(next);
            setClerkArchived(token, caseId, next).catch(() => setCaseArchived(!next));
          }}
        />
      </div>
    </div>
  );
}

/** Service-of-process hold (court cases). Until the Plaintiff confirms the
 *  Defendant was served, the engine refuses to contact the Defendant — but
 *  it can't block a staffer who picks up the phone, so this banner loudly
 *  says "do not contact" and gives the one button that lifts the hold
 *  (POST /confirm-service, audited). On success the case is re-fetched so
 *  the banner clears. A 409 NOT_ON_HOLD (already confirmed elsewhere) is
 *  treated as success — just refresh. */
function ServiceHoldBanner({
  token,
  caseId,
  canMutate,
  onConfirmed,
  onUnauthorized,
}: {
  token: string;
  caseId: string;
  canMutate: boolean;
  onConfirmed: () => void;
  onUnauthorized: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const markServed = async () => {
    const ok = await confirmDialog(
      'Confirm the Defendant(s) were served with process? This lifts the hold and lets Levelheaded begin contacting the Defendant.',
      { title: 'Mark Defendant served?', confirmLabel: 'Mark served' },
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const res = await confirmService(token, caseId);
      onConfirmed();
      window.alert(
        res.needsDefendantInfo
          ? "Service confirmed — hold lifted. Add the Defendant's contact info to start outreach."
          : res.defendantOutreachStarted
            ? 'Service confirmed — hold lifted. Defendant outreach has started.'
            : 'Service confirmed — hold lifted.',
      );
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) {
        onUnauthorized();
        return;
      }
      // Already confirmed elsewhere — refresh so the banner clears.
      if (err instanceof EngineError && err.code === 'NOT_ON_HOLD') {
        onConfirmed();
        return;
      }
      setError(err instanceof EngineError ? err.message : 'Could not mark the Defendant served.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-red-300 bg-red-50 p-3" role="alert">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-red-800">⚠️ Defendant not served — do not contact</div>
          <p className="mt-1 text-xs text-red-700">
            We are not reaching out to the Defendant(s) until service of process is confirmed. Do not call, text, or
            email them yet.
          </p>
          {error && <p className="mt-1.5 text-xs font-medium text-red-700">{error}</p>}
        </div>
        {canMutate && (
          <button
            type="button"
            className="btn-primary shrink-0 !bg-red-600 !py-1 !text-xs hover:!bg-red-700"
            disabled={busy}
            onClick={() => void markServed()}
          >
            {busy ? 'Working…' : 'Mark Defendant served'}
          </button>
        )}
      </div>
    </div>
  );
}

/** Revive a closed case (Danny, 2026-09-01: parties re-emerged after a
 *  ROM auto-close with trial still ahead). Requires a reason; the case
 *  returns to EVALUATING — scheduling and payment work again. */
function ReopenCaseControl({
  token,
  caseId,
  onReopened,
}: {
  token: string;
  caseId: string;
  onReopened: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return (
      <button type="button" className="btn-secondary !text-xs" onClick={() => setOpen(true)}>
        Reopen case…
      </button>
    );
  }
  return (
    <div className="w-full max-w-md rounded-md border border-surface-border bg-surface-muted p-2 text-left">
      <div className="text-xs font-semibold">Reopen this case?</div>
      <div className="mt-0.5 text-[11px] text-gray-600">
        The case returns to the scheduling/payment stage; the closed status is cleared from the
        court view. A reason is required (audit trail + case notes).
      </div>
      <textarea
        className="mt-1.5 w-full rounded-md border border-surface-border px-2 py-1 text-xs"
        rows={2}
        placeholder="Why is this case being reopened? (required)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      {error && <div className="mt-1 text-[11px] text-red-600">{error}</div>}
      <div className="mt-1.5 flex justify-end gap-1.5">
        <button type="button" className="btn-secondary !py-1 !text-xs" disabled={busy} onClick={() => { setOpen(false); setReason(''); setError(null); }}>
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary !py-1 !text-xs"
          disabled={busy || !reason.trim()}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await reopenCase(token, caseId, reason.trim());
              setOpen(false);
              onReopened();
            } catch (err) {
              setError(err instanceof EngineError ? err.message : 'Could not reopen the case.');
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Reopening…' : 'Reopen case'}
        </button>
      </div>
    </div>
  );
}

/** Close the case for good: status → CLOSED through the engine's state
 *  machine, comms stop, and a REQUIRED reason lands in the audit trail
 *  and the Notes panel (Danny, 2026-08-27). Irreversible, hence the
 *  two-step with a mandatory why. */
function CloseCaseControl({
  token,
  caseId,
  onClosed,
}: {
  token: string;
  caseId: string;
  onClosed: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [closedStatus, setClosedStatus] = useState<ClosedStatusKey | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return (
      <button type="button" className="btn-secondary !text-xs" onClick={() => setOpen(true)}>
        Close case…
      </button>
    );
  }

  return (
    <div className="w-full max-w-md rounded-md border border-red-200 bg-red-50 p-2 text-left">
      <div className="text-xs font-semibold text-red-800">Close this case?</div>
      <div className="mt-0.5 text-[11px] text-red-700">
        This is final: the case moves to Closed, and scheduled messages and reminders stop.
        A reason is required — it goes in the audit trail and the case notes.
      </div>
      <select
        className="mt-1.5 w-full rounded-md border border-surface-border bg-white px-2 py-1 text-xs"
        value={closedStatus}
        onChange={(e) => setClosedStatus(e.target.value as ClosedStatusKey | '')}
      >
        <option value="">Closed status (required — shown to the court)…</option>
        {CLOSED_STATUS_OPTIONS.map((o) => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
      </select>
      <textarea
        className="mt-1.5 w-full rounded-md border border-surface-border px-2 py-1 text-xs"
        rows={3}
        placeholder="Why is this case being closed? (required)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      {error && <div className="mt-1 text-[11px] text-red-700">{error}</div>}
      <div className="mt-1.5 flex justify-end gap-1.5">
        <button
          type="button"
          className="btn-secondary !py-1 !text-xs"
          disabled={busy}
          onClick={() => {
            setOpen(false);
            setReason('');
            setError(null);
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary !bg-red-700 !py-1 !text-xs"
          disabled={busy || !reason.trim() || !closedStatus}
          onClick={async () => {
            if (!closedStatus) return;
            setBusy(true);
            setError(null);
            try {
              await closeCase(token, caseId, reason.trim(), closedStatus);
              setOpen(false);
              onClosed();
            } catch (err) {
              setError(err instanceof EngineError ? err.message : 'Could not close the case.');
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Closing…' : 'Close case'}
        </button>
      </div>
    </div>
  );
}

/** Start a Zoom session right now (vs the booked flow): meeting is
 *  created immediately, linked to the case (sidebar + RTMS match on the
 *  meeting id). The mediator opens the host link and shares the join
 *  link deliberately — nothing is auto-sent to parties. */
function InstantSessionControl({
  token,
  caseId,
  parties,
  onUnauthorized,
}: {
  token: string;
  caseId: string;
  parties: Party[];
  onUnauthorized: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [armed, setArmed] = useState(false);
  const [links, setLinks] = useState<{ start: string | null; join: string; phoneJoin: { number: string; passcode: string | null; meeting_id: string } | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [invites, setInvites] = useState<Record<string, 'sending' | 'sent' | string>>({});

  const invite = async (party: Party) => {
    if (!links) return;
    setInvites((prev) => ({ ...prev, [party.id]: 'sending' }));
    try {
      // Dial-in fallback for a party who can't launch Zoom (Danny,
      // 2026-09-02: the instant-invite email had no phone number).
      const pj = links.phoneJoin;
      const dialLine = pj
        ? ` Or join by phone: ${pj.number}, meeting ID ${pj.meeting_id.replace(/(\d{3})(\d{4})(\d+)/, '$1 $2 $3')}${pj.passcode ? `, passcode ${pj.passcode}` : ''}.`
        : '';
      const body = `Your mediation session with Levelheaded is starting now. Join here: ${links.join}.${dialLine}`;
      // Hit them where they're at: every channel they gave us.
      const sends: Array<Promise<unknown>> = [];
      if (party.phone) {
        sends.push(sendAction(token, caseId, { recipient_party_ids: [party.id], channel: 'sms', body }));
      }
      if (party.email) {
        sends.push(
          sendAction(token, caseId, {
            recipient_party_ids: [party.id],
            channel: 'email',
            subject: 'Your mediation session is starting',
            body,
          }),
        );
      }
      if (!sends.length) {
        setInvites((prev) => ({ ...prev, [party.id]: 'No contact info on file' }));
        return;
      }
      await Promise.all(sends);
      setInvites((prev) => ({ ...prev, [party.id]: 'sent' }));
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) {
        onUnauthorized();
        return;
      }
      setInvites((prev) => ({
        ...prev,
        [party.id]: err instanceof EngineError ? err.message : 'Send failed',
      }));
    }
  };

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await startInstantSession(token, caseId);
      setLinks({ start: res.start_url, join: res.join_url, phoneJoin: res.phone_join });
      // Best effort — silently swallowed inside the Zoom client webview,
      // which is why the links also render below with copy buttons.
      if (res.start_url) window.open(res.start_url, '_blank');
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) {
        onUnauthorized();
        return;
      }
      setError(err instanceof EngineError ? err.message : 'Could not start a session.');
    } finally {
      setBusy(false);
    }
  };

  if (links) {
    return (
      <div className="mt-2 border-t border-surface-border pt-2 text-xs">
        <div className="font-medium text-green-700">Session created ✓</div>
        {links.start && (
          <>
            <a href={links.start} target="_blank" rel="noreferrer" className="btn-primary mt-1.5 w-full !py-1 !text-xs">
              Open as host
            </a>
            <button
              type="button"
              className="btn-secondary mt-1 w-full !py-0.5 !text-xs"
              onClick={() => void navigator.clipboard?.writeText(links.start!).catch(() => {})}
            >
              Copy host link (if the button above does nothing — Zoom client)
            </button>
          </>
        )}
        <div className="mt-1.5 flex items-center gap-1.5">
          <code className="min-w-0 flex-1 truncate text-[11px]">{links.join}</code>
          <button
            type="button"
            className="btn-secondary !py-0.5 !text-xs"
            onClick={() => void navigator.clipboard?.writeText(links.join).catch(() => {})}
          >
            Copy join link
          </button>
        </div>
        <div className="mt-2 space-y-1">
          {parties.map((p) => {
            const state = invites[p.id];
            return (
              <div key={p.id} className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate">{p.name}</span>
                {state === 'sent' ? (
                  <span className="shrink-0 font-medium text-green-700">Invited ✓</span>
                ) : (
                  <button
                    type="button"
                    className="btn-secondary shrink-0 !py-0.5 !text-xs"
                    disabled={state === 'sending'}
                    onClick={() => void invite(p)}
                  >
                    {state === 'sending' ? 'Sending…' : 'Text/email invite'}
                  </button>
                )}
                {state && state !== 'sending' && state !== 'sent' && (
                  <span className="shrink-0 text-red-600">{state}</span>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-1 text-gray-400">
          Invites go through Levelheaded&rsquo;s rails — delivery shows on the timeline.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-2 border-t border-surface-border pt-2">
      {/* Two-step inline confirm — window.confirm is silently suppressed
          in the Zoom client webview, which made the button feel dead. */}
      {!armed ? (
        <button
          type="button"
          className="btn-secondary w-full !py-1 !text-xs"
          disabled={busy}
          onClick={() => setArmed(true)}
        >
          Start instant session
        </button>
      ) : (
        <div className="flex gap-1.5">
          <button type="button" className="btn-secondary flex-1 !py-1 !text-xs" disabled={busy} onClick={() => setArmed(false)}>
            Cancel
          </button>
          <button type="button" className="btn-primary flex-1 !py-1 !text-xs" disabled={busy} onClick={() => void start()}>
            {busy ? 'Creating…' : 'Yes, start now'}
          </button>
        </div>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

/** Inline edit of a party's contact card (name, email, phone; relationship
 *  for added participants). Clearing a field empties it server-side. */
/** Role choices per domain pack — each maps display label → (role text,
    structural side). 'Other' intakes get sorted out here. */
const ROLE_CHOICES: Record<string, Array<{ label: string; role: string; archetype: 'claimant' | 'respondent' }>> = {
  court_dispute: [
    { label: 'Plaintiff', role: 'plaintiff', archetype: 'claimant' },
    { label: 'Defendant', role: 'defendant', archetype: 'respondent' },
    { label: 'Other (claimant side)', role: 'other', archetype: 'claimant' },
    { label: 'Other (respondent side)', role: 'other', archetype: 'respondent' },
  ],
  rent_delinquency: [
    { label: 'Landlord / Property owner', role: 'landlord', archetype: 'claimant' },
    { label: 'Tenant', role: 'tenant', archetype: 'respondent' },
  ],
};

function EditPartyForm({
  token,
  caseId,
  party,
  siblings,
  domainPack,
  onSaved,
  onCancel,
  onDeleted,
  onUnauthorized,
}: {
  token: string;
  caseId: string;
  party: Party;
  siblings: Party[];
  domainPack?: string;
  onSaved: (party: Party) => void;
  onCancel: () => void;
  onDeleted: () => void;
  onUnauthorized: () => void;
}) {
  const isParticipant = party.role_archetype === 'participant';
  const isPrincipal = ['claimant', 'respondent'].includes(party.role_archetype);
  // Selector defaults mirror the engine's resolveSelectors: principals are
  // asked unless explicitly turned off; others only when opted in or acting
  // as a proxy. Show the live effective state so the toggle isn't misleading.
  const [isSelector, setIsSelector] = useState<boolean>(
    typeof party.metadata?.is_selector === 'boolean' ? party.metadata.is_selector : isPrincipal,
  );
  const [actsFor, setActsFor] = useState<string[]>(
    Array.isArray(party.metadata?.acts_for) ? (party.metadata.acts_for as string[]) : [],
  );
  // Invoicing default mirrors the engine: principals are billed unless
  // explicitly turned off; others only when opted in (Rachel, 2026-09-14:
  // entity defendants / attorney-represented parties).
  const [receivesInvoice, setReceivesInvoice] = useState<boolean>(
    typeof party.metadata?.invoice_recipient === 'boolean' ? party.metadata.invoice_recipient : isPrincipal,
  );
  const roleChoices = ROLE_CHOICES[domainPack ?? ''] ?? [
    { label: 'Claimant', role: 'claimant', archetype: 'claimant' as const },
    { label: 'Respondent', role: 'respondent', archetype: 'respondent' as const },
  ];
  const currentChoice = roleChoices.findIndex(
    (rc) => rc.role === party.role && rc.archetype === party.role_archetype,
  );
  const [roleIdx, setRoleIdx] = useState<number>(currentChoice);
  const [name, setName] = useState(party.name);
  const [email, setEmail] = useState(party.email ?? '');
  const [phone, setPhone] = useState(party.phone ?? '');
  const [relationship, setRelationship] = useState(
    typeof party.metadata?.relationship === 'string' ? party.metadata.relationship : '',
  );
  const [businessName, setBusinessName] = useState(
    typeof party.metadata?.business_name === 'string' ? party.metadata.business_name : '',
  );
  const [businessRole, setBusinessRole] = useState(
    typeof party.metadata?.business_role === 'string' ? party.metadata.business_role : '',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Two-press delete (Danny, 2026-09-21): the first press arms an inline
  // confirm strip spelling out what goes with the party; only the second,
  // differently-worded press deletes. No browser dialogs (Zoom webview).
  const [deleteArmed, setDeleteArmed] = useState(false);

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteCaseParty(token, caseId, party.id);
      onDeleted();
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) {
        onUnauthorized();
        return;
      }
      setError(err instanceof EngineError ? err.message : 'Could not delete this party.');
      setDeleteArmed(false);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const roleChanged = roleIdx >= 0 && roleIdx !== currentChoice;
      const updated = await updateCaseParty(token, caseId, party.id, {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        ...(isParticipant ? { relationship: relationship.trim() } : {}),
        business_name: businessName.trim(),
        business_role: businessRole.trim(),
        is_selector: isSelector,
        acts_for: actsFor,
        invoice_recipient: receivesInvoice,
        ...(roleChanged
          ? { role: roleChoices[roleIdx].role, role_archetype: roleChoices[roleIdx].archetype }
          : {}),
      });
      onSaved(updated);
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) {
        onUnauthorized();
        return;
      }
      setError(err instanceof EngineError ? err.message : 'Could not save changes.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="card p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() && !busy) void save();
      }}
    >
      <div className="text-sm font-semibold">Edit {party.name}</div>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <input
          className="rounded-md border border-surface-border px-3 py-2 text-sm"
          placeholder="Full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={busy}
        />
        <select
          className="rounded-md border border-surface-border px-3 py-2 text-sm"
          value={roleIdx}
          onChange={(e) => setRoleIdx(Number(e.target.value))}
          disabled={busy}
          aria-label="Role"
        >
          {/* Participants can be PROMOTED to a principal side (found live
              2026-08-28: the defendant's PM was added as a participant, so
              scheduling saw no defendant). Promoting makes them schedulable
              and A2M-gated like any principal. */}
          {roleIdx < 0 && (
            <option value={-1}>
              {isParticipant ? 'Participant (support person)…' : `Role: ${party.role || 'unset'}…`}
            </option>
          )}
          {roleChoices.map((rc, i) => (
            <option key={i} value={i}>
              {isParticipant ? `Promote to ${rc.label}` : rc.label}
            </option>
          ))}
        </select>
        {isParticipant && (
          <input
            className="rounded-md border border-surface-border px-3 py-2 text-sm"
            placeholder="Relationship"
            value={relationship}
            onChange={(e) => setRelationship(e.target.value)}
            disabled={busy}
          />
        )}
        <input
          className="rounded-md border border-surface-border px-3 py-2 text-sm"
          placeholder="Email (blank to clear)"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
        />
        <input
          className="rounded-md border border-surface-border px-3 py-2 text-sm"
          placeholder="Business / entity name (e.g. Flats at Sky Village)"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          disabled={busy}
        />
        <input
          className="rounded-md border border-surface-border px-3 py-2 text-sm"
          placeholder="Their role there (e.g. Property Manager)"
          value={businessRole}
          onChange={(e) => setBusinessRole(e.target.value)}
          disabled={busy}
        />
        <input
          className="rounded-md border border-surface-border px-3 py-2 text-sm"
          placeholder="Mobile phone (blank to clear)"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={busy}
        />
      </div>
      <div className="mt-3 rounded-md border border-surface-border bg-surface-muted p-2.5">
        <div className="text-xs font-semibold text-gray-700">Scheduling</div>
        <label className="mt-1.5 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={isSelector}
            onChange={(e) => setIsSelector(e.target.checked)}
            disabled={busy}
          />
          <span>
            Ask this person for their availability
            <span className="block text-[11px] text-gray-500">
              Turn off for someone who is scheduled by another person (e.g. a client whose attorney picks times).
            </span>
          </span>
        </label>
        <label className="mt-2 flex items-start gap-2 border-t border-surface-border pt-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={receivesInvoice}
            onChange={(e) => setReceivesInvoice(e.target.checked)}
            disabled={busy}
          />
          <span>
            Receives invoices
            <span className="block text-[11px] text-gray-500">
              Who gets the mediation fee bill. Turn off for an entity or represented party and turn it on for whoever actually pays (e.g. their attorney).
            </span>
          </span>
        </label>
        {siblings.length > 0 && (
          <div className="mt-2">
            <div className="text-[11px] font-medium text-gray-600">
              Schedules on behalf of{' '}
              <span className="font-normal text-gray-400">(they will be asked, these people won't)</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {siblings.map((s) => {
                const on = actsFor.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      setActsFor((prev) => (on ? prev.filter((id) => id !== s.id) : [...prev, s.id]))
                    }
                    className={`rounded-full border px-2 py-0.5 text-xs ${
                      on
                        ? 'border-brand bg-brand/10 text-brand'
                        : 'border-surface-border text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    {on ? '✓ ' : ''}
                    {s.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {error && <div className="mt-1.5 text-xs text-red-600">{error}</div>}
      {deleteArmed ? (
        <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-2.5">
          <div className="text-xs font-medium text-red-800">
            Permanently delete {party.name} from this case?
          </div>
          <div className="mt-0.5 text-[11px] text-red-700">
            Their message threads, scheduling availability, and any unpaid invoices are removed with
            them. This cannot be undone. Parties with payments or signed documents cannot be deleted.
          </div>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              className="btn-secondary !py-1 !text-xs"
              onClick={() => setDeleteArmed(false)}
              disabled={busy}
            >
              Keep {party.name.split(' ')[0]}
            </button>
            <button
              type="button"
              className="btn-primary !bg-red-600 !py-1 !text-xs hover:!bg-red-700"
              onClick={() => void remove()}
              disabled={busy}
            >
              {busy ? 'Deleting…' : `Yes — delete ${party.name.split(' ')[0]} permanently`}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-2">
          <button type="button" className="btn-secondary !py-1 !text-xs" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn-primary !py-1 !text-xs" disabled={busy || !name.trim()}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
          <button
            type="button"
            className="ml-auto text-xs font-medium text-red-600 hover:underline"
            onClick={() => setDeleteArmed(true)}
            disabled={busy}
          >
            Delete party…
          </button>
        </div>
      )}
    </form>
  );
}

/** Mediator adds an additional participant: full name + free-form
 *  relationship ("Roommate of tenant", "Lawyer for plaintiff"). They join
 *  the composer's recipient list on their own private thread; the
 *  two-party scheduling machinery ignores them. */
function AddPartyForm({
  token,
  caseId,
  onAdded,
  onUnauthorized,
}: {
  token: string;
  caseId: string;
  onAdded: (party: Party) => void;
  onUnauthorized: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [side, setSide] = useState<'participant' | 'claimant' | 'respondent'>('participant');
  const [relationship, setRelationship] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return (
      <button type="button" className="btn-secondary !py-1 !text-xs" onClick={() => setOpen(true)}>
        + Add person
      </button>
    );
  }

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const party = await addCaseParty(token, caseId, {
        name: name.trim(),
        side,
        ...(side === 'participant' ? { relationship: relationship.trim() } : {}),
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
      });
      onAdded(party);
      setOpen(false);
      setName(''); setRelationship(''); setEmail(''); setPhone('');
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) {
        onUnauthorized();
        return;
      }
      setError(err instanceof EngineError ? err.message : 'Could not add this person.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="card p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() && (side !== 'participant' || relationship.trim()) && !busy) void submit();
      }}
    >
      <div className="text-sm font-semibold">Add a person to this case</div>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <input
          className="rounded-md border border-surface-border px-3 py-2 text-sm"
          placeholder="Full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={busy}
        />
        <select
          className="rounded-md border border-surface-border px-3 py-2 text-sm"
          value={side}
          onChange={(e) => setSide(e.target.value as 'participant' | 'claimant' | 'respondent')}
          disabled={busy}
          title="A Plaintiff or Defendant joins scheduling, documents, and signatures. Other participants (lawyers, roommates, family) get their own message thread but stay out of the two-party machinery."
        >
          <option value="participant">Other participant (lawyer, roommate…)</option>
          <option value="claimant">Plaintiff / Claimant</option>
          <option value="respondent">Defendant / Respondent</option>
        </select>
        {side === 'participant' && (
          <input
            className="rounded-md border border-surface-border px-3 py-2 text-sm"
            placeholder="Relationship (e.g. Lawyer for plaintiff, roommate)"
            value={relationship}
            onChange={(e) => setRelationship(e.target.value)}
            disabled={busy}
          />
        )}
        <input
          className="rounded-md border border-surface-border px-3 py-2 text-sm"
          placeholder="Email (optional)"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
        />
        <input
          className="rounded-md border border-surface-border px-3 py-2 text-sm"
          placeholder="Mobile phone (optional)"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={busy}
        />
      </div>
      <p className="mt-1.5 text-xs text-gray-500">
        Give at least one contact method to message them — they get their own private thread.
      </p>
      {error && <div className="mt-1.5 text-xs text-red-600">{error}</div>}
      <div className="mt-2 flex gap-2">
        <button type="button" className="btn-secondary !py-1 !text-xs" onClick={() => setOpen(false)} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="btn-primary !py-1 !text-xs" disabled={busy || !name.trim() || (side === 'participant' && !relationship.trim())}>
          {busy ? 'Adding…' : 'Add person'}
        </button>
      </div>
    </form>
  );
}

/** Engine PartyInvoiceStatus is uppercase: UNPAID | PAID | VOID | REFUNDED. */
function InvoiceChip({ invoice }: { invoice: PartyInvoice }) {
  const { label, tone } = {
    PAID: { label: 'Paid ✓', tone: 'bg-green-100 text-green-800' },
    REFUNDED: { label: 'Refunded', tone: 'bg-gray-100 text-gray-600' },
    VOID: { label: 'Canceled', tone: 'bg-gray-100 text-gray-500 line-through' },
    UNPAID: { label: 'Unpaid', tone: 'bg-amber-100 text-amber-800' },
  }[invoice.status] ?? { label: invoice.status, tone: 'bg-amber-100 text-amber-800' };
  return <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>{label}</span>;
}

/** Assign/reassign the case's mediator (Danny, 2026-08-24). Options are
    the source's active mediator/admin roster; every change is audited. */
function MediatorAssignPanel({
  token,
  caseId,
  orgId,
  assignedId,
  canMutate,
}: {
  token: string;
  caseId: string;
  orgId: string;
  assignedId: string | null;
  canMutate: boolean;
}) {
  const [roster, setRoster] = useState<Array<{ id: string; display_name: string }> | null>(null);
  const [current, setCurrent] = useState<string | null>(assignedId);
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => {
    getMediatorRoster(token, orgId)
      .then((r) => setRoster(r.mediators))
      .catch(() => setRoster([]));
  }, [token, orgId]);
  const name = roster?.find((m) => m.id === current)?.display_name;
  return (
    <section className="card p-4">
      <div className="section-label">Mediator</div>
      {!canMutate ? (
        <p className="mt-1 text-sm">{name ?? 'Unassigned'}</p>
      ) : (
        <>
          <select
            className="mt-2 w-full rounded-md border border-surface-border px-2 py-1 text-sm"
            value={current ?? ''}
            onChange={(e) => {
              const next = e.target.value || null;
              const prev = current;
              setCurrent(next);
              setNote(null);
              setNote('Reassigning…');
              assignCaseMediator(token, caseId, next)
                .then((res) => {
                  if (res.session_reassigned === 'rebuilt') setNote('Saved ✓ — booked session moved to their Zoom & calendar');
                  else if (res.session_reassigned === 'failed') setNote(`Assigned, but the booked session could NOT move: ${res.session_reassign_error ?? 'unknown'}. Rebook or use the scheduling panel.`);
                  else setNote('Saved ✓');
                })
                .catch((err: unknown) => {
                  setCurrent(prev);
                  setNote(err instanceof EngineError ? err.message : 'Could not reassign.');
                });
            }}
          >
            <option value="">Unassigned</option>
            {(roster ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.display_name}
              </option>
            ))}
          </select>
          {note && <p className="mt-1 text-xs text-gray-500">{note}</p>}
        </>
      )}
    </section>
  );
}

/** Court-date correction (Danny, 2026-08-24 — Mo found a case with the
    wrong trial date). Drives scheduling windows and trial-pressure
    flags; audited old → new server-side. */
/** "+ Log call" under each party (Danny, 2026-08-28): records a phone
 *  attempt with outcome + note as the call.logged audit entry — the same
 *  entry the Certificate of Attempts counts as a "Phone at date/time"
 *  line. Inline, no new panel. */
// Place a tracked call to a party FROM the source number, via the dial-in
// bridge. First use prompts a one-time cell verification (the bridge
// authenticates the mediator by their verified caller ID); after that it's
// a tap-to-dial link (mobile) + copyable string (desktop).
/** Shareable deep link to this case (Danny, 2026-10-14 — drop into Slack).
    Resolves against whatever dashboard origin the viewer is on; DashboardApp
    reads ?open_case and jumps straight to the case. Available to every role. */
function CopyCaseLink({ caseId }: { caseId: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    const url = `${window.location.origin}/?open_case=${caseId}`;
    const done = () => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(() => window.prompt('Copy the case link:', url));
    } else {
      window.prompt('Copy the case link:', url);
    }
  };
  return (
    <button
      type="button"
      className="shrink-0 rounded-md border border-surface-border px-2 py-0.5 text-xs font-medium text-gray-600 hover:border-gray-300 hover:text-brand"
      title="Copy a shareable link to this case (paste into Slack, etc.)"
      onClick={copy}
    >
      {copied ? '✓ Link copied' : '🔗 Copy link'}
    </button>
  );
}

function CallControl({ token, caseId, party }: { token: string; caseId: string; party: Party }) {
  const [mode, setMode] = useState<'idle' | 'result' | 'reg-phone' | 'reg-code'>('idle');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ dial_string: string; source_number: string | null } | null>(null);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');

  const place = () => {
    setBusy(true);
    setError(null);
    callParty(token, caseId, party.id)
      .then((r) => {
        if (!r.caller_registered) { setMode('reg-phone'); return; }
        setResult({ dial_string: r.dial_string, source_number: r.source_number });
        setMode('result');
      })
      .catch((err) => setError(err instanceof EngineError ? err.message : 'Could not start the call.'))
      .finally(() => setBusy(false));
  };

  if (mode === 'idle') {
    return (
      <button type="button" className="text-xs text-gray-400 hover:text-brand" disabled={busy} onClick={place}>
        {busy ? '…' : '📞 Call'}
      </button>
    );
  }
  if (mode === 'reg-phone' || mode === 'reg-code') {
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-amber-700">Register your cell once to call:</span>
        {mode === 'reg-phone' ? (
          <>
            <input className="w-32 rounded-md border border-surface-border px-1.5 py-0.5 text-xs" placeholder="Your mobile #" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={busy} />
            <button type="button" className="btn-primary !py-0.5 !text-xs" disabled={busy || !phone.trim()} onClick={() => { setBusy(true); setError(null); startPhoneVerification(token, phone.trim()).then(() => setMode('reg-code')).catch((err) => setError(err instanceof EngineError ? err.message : 'Could not send a code.')).finally(() => setBusy(false)); }}>
              {busy ? 'Sending…' : 'Text me a code'}
            </button>
          </>
        ) : (
          <>
            <input className="w-20 rounded-md border border-surface-border px-1.5 py-0.5 text-xs" placeholder="Code" value={code} onChange={(e) => setCode(e.target.value)} disabled={busy} />
            <button type="button" className="btn-primary !py-0.5 !text-xs" disabled={busy || !code.trim()} onClick={() => { setBusy(true); setError(null); verifyPhone(token, phone.trim(), code.trim()).then(() => place()).catch((err) => setError(err instanceof EngineError ? err.message : 'That code did not verify.')).finally(() => setBusy(false)); }}>
              {busy ? 'Verifying…' : 'Verify & call'}
            </button>
          </>
        )}
        <button type="button" className="text-xs text-gray-400 hover:text-brand" onClick={() => { setMode('idle'); setError(null); }}>Cancel</button>
        {error && <span className="text-xs text-red-600">{error}</span>}
      </span>
    );
  }
  // result
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <a className="btn-primary !py-0.5 !text-xs no-underline" href={`tel:${result?.dial_string ?? ''}`}>📞 Tap to call {party.name.split(' ')[0]}</a>
      <button type="button" className="text-xs text-gray-500 underline decoration-dotted hover:text-brand" onClick={() => { if (result) void navigator.clipboard?.writeText(result.dial_string).catch(() => {}); }}>copy dial string</button>
      <span className="text-[11px] text-gray-400">Party sees {result?.source_number ?? 'the source number'}; your cell stays private.</span>
      <button type="button" className="text-xs text-gray-400 hover:text-brand" onClick={() => setMode('idle')}>done</button>
    </span>
  );
}

function LogCallControl({
  token,
  caseId,
  party,
  onLogged,
}: {
  token: string;
  caseId: string;
  party: Party;
  onLogged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState<'no_answer' | 'voicemail' | 'spoke' | 'bad_number'>('no_answer');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return (
      <span className="inline-flex items-center gap-1">
        <button
          type="button"
          className="text-xs text-gray-400 hover:text-brand"
          onClick={() => {
            setOpen(true);
            setSaved(false);
          }}
        >
          + Log call
        </button>
        {saved && <span className="text-xs text-green-700">Logged ✓</span>}
      </span>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <select
        className="rounded-md border border-surface-border px-1.5 py-0.5 text-xs"
        value={outcome}
        onChange={(e) => setOutcome(e.target.value as typeof outcome)}
        disabled={busy}
        aria-label="Call outcome"
      >
        <option value="no_answer">No answer</option>
        <option value="voicemail">Voicemail left</option>
        <option value="spoke">Spoke with them</option>
        <option value="bad_number">Bad number</option>
      </select>
      <input
        className="w-48 rounded-md border border-surface-border px-1.5 py-0.5 text-xs"
        placeholder="Note (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        disabled={busy}
      />
      <button
        type="button"
        className="btn-primary !py-0.5 !text-xs"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setError(null);
          logPartyCall(token, caseId, party.id, outcome, note.trim() || undefined)
            .then(() => {
              setOpen(false);
              setNote('');
              setOutcome('no_answer');
              setSaved(true);
              onLogged();
            })
            .catch((err) => setError(err instanceof EngineError ? err.message : 'Could not log the call.'))
            .finally(() => setBusy(false));
        }}
      >
        {busy ? 'Logging…' : 'Log'}
      </button>
      <button type="button" className="btn-secondary !py-0.5 !text-xs" disabled={busy} onClick={() => setOpen(false)}>
        Cancel
      </button>
      {error && <span className="text-xs text-red-600">{error}</span>}
      {saved && <span className="text-xs text-green-700">Logged ✓</span>}
    </div>
  );
}

/** Missing-party acquisition (§8, Mo's Q10): for a party we have no way to
 *  reach, run ONE good-faith internet search for a BUSINESS public contact.
 *  Businesses only (the engine rejects individuals with 422). The result is
 *  a candidate the mediator reviews and applies deliberately — never
 *  auto-applied, never auto-emailed. Shows any prior search result. */
function FindContactControl({
  token,
  caseId,
  party,
  onApplied,
  onSearched,
  onUnauthorized,
}: {
  token: string;
  caseId: string;
  party: Party;
  onApplied: (party: Party) => void;
  onSearched: () => void;
  onUnauthorized: () => void;
}) {
  const priorRaw = (party.metadata?.business_contact_candidate ?? null) as
    | (BusinessContactCandidate & { searched_by?: string })
    | null;
  const [candidate, setCandidate] = useState<BusinessContactCandidate | null>(priorRaw);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runSearch = () => {
    setBusy(true);
    setError(null);
    findPartyContact(token, caseId, party.id)
      .then((res) => {
        setCandidate(res.candidate);
        onSearched();
      })
      .catch((err) => {
        if (err instanceof EngineError && err.status === 401) return onUnauthorized();
        setError(err instanceof EngineError ? err.message : 'Contact search failed.');
      })
      .finally(() => setBusy(false));
  };

  const apply = () => {
    if (!candidate) return;
    setApplying(true);
    setError(null);
    updateCaseParty(token, caseId, party.id, {
      email: candidate.email ?? '',
      phone: candidate.phone ?? '',
    })
      .then((updated) => {
        setCandidate(null);
        onApplied(updated);
      })
      .catch((err) => {
        if (err instanceof EngineError && err.status === 401) return onUnauthorized();
        setError(err instanceof EngineError ? err.message : 'Could not apply the contact.');
      })
      .finally(() => setApplying(false));
  };

  return (
    <span className="inline-flex flex-col gap-1">
      <span className="inline-flex items-center gap-2">
        <button type="button" className="text-xs text-gray-400 hover:text-brand" disabled={busy} onClick={runSearch}>
          {busy ? 'Searching…' : candidate ? 'Search again' : '🔎 Find business contact'}
        </button>
        {error && <span className="text-xs text-red-600">{error}</span>}
      </span>
      {candidate && (
        <div className="max-w-md rounded-md border border-surface-border bg-surface-muted p-2 text-xs">
          {candidate.found ? (
            <>
              <div className="font-medium text-gray-700">Found a public contact — review before using</div>
              <div className="mt-1 space-y-0.5 text-gray-600">
                {candidate.email && <div>✉ {candidate.email}</div>}
                {candidate.phone && <div>📞 {candidate.phone}</div>}
                {candidate.website && <div>🌐 {candidate.website}</div>}
                {candidate.source_url && (
                  <div className="truncate">
                    Source:{' '}
                    <a href={candidate.source_url} target="_blank" rel="noreferrer" className="text-brand underline">
                      {candidate.source_url}
                    </a>
                  </div>
                )}
                {candidate.note && <div className="text-gray-500">{candidate.note}</div>}
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                <button type="button" className="btn-primary !py-0.5 !text-xs" disabled={applying} onClick={apply}>
                  {applying ? 'Applying…' : 'Use this contact'}
                </button>
                <button type="button" className="text-xs text-gray-400 hover:text-gray-600" onClick={() => setCandidate(null)}>
                  Dismiss
                </button>
              </div>
            </>
          ) : (
            <div className="text-gray-500">
              No confident public contact found. {candidate.note} Ask the responding party for the contact, or hold for review.
            </div>
          )}
        </div>
      )}
    </span>
  );
}

/** §8 business coordination: once we have a reachable contact for a BUSINESS
    party (a web-found candidate or an email/phone on file), send a formal
    "To Whom It May Concern" note asking them to route us to the right person,
    with a 3-business-day deadline. If no reply lands by the deadline the engine
    generates a Certificate of Attempts and flags the mediator to notify the
    Court. BUSINESSES only; the note goes to the web-found contact. */
function CoordinationNoteControl({
  token,
  caseId,
  party,
  canMutate,
  onSent,
  onUnauthorized,
}: {
  token: string;
  caseId: string;
  party: Party;
  canMutate: boolean;
  onSent: () => void;
  onUnauthorized: () => void;
}) {
  const note = (party.metadata?.coordination_note ?? null) as {
    sent_at?: string;
    due_at?: string;
    to?: { email?: string | null; phone?: string | null };
    queued?: number;
    responded_at?: string;
    escalated_at?: string;
  } | null;
  const candidate = (party.metadata?.business_contact_candidate ?? null) as BusinessContactCandidate | null;
  const hasReachable =
    Boolean(party.email || party.phone) || Boolean(candidate?.found && (candidate.email || candidate.phone));

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Once a note has gone out, the status line takes the place of the button.
  if (note?.sent_at) {
    return (
      <div className="text-xs">
        {note.escalated_at ? (
          <span className="text-red-600">No reply — Certificate of Attempts generated; notify the Court.</span>
        ) : note.responded_at ? (
          <span className="text-teal-700">Business replied {formatDate(note.responded_at)}.</span>
        ) : (
          <span className="text-gray-500">
            Coordination note sent {formatDate(note.sent_at)} — awaiting reply
            {note.due_at ? ` (Court notified if none by ${formatDate(note.due_at)})` : ''}.
          </span>
        )}
      </div>
    );
  }

  if (!canMutate || !hasReachable) return null;

  const send = async () => {
    const ok = await confirmDialog(
      `Send ${party.name} a formal coordination note asking them to route us to the right person? They have 3 business days to reply before a Certificate of Attempts is generated.`,
      { title: 'Send coordination note?', confirmLabel: 'Send note' },
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const res = await sendCoordinationNote(token, caseId, party.id);
      onSent();
      window.alert(
        `Coordination note sent.${res.due_at ? ` If they don't reply by ${formatDate(res.due_at)}, a Certificate of Attempts is generated and you'll be flagged to notify the Court.` : ''}`,
      );
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Could not send the coordination note.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-flex flex-col gap-0.5">
      <button
        type="button"
        className="text-left text-xs text-gray-400 hover:text-brand disabled:opacity-50"
        disabled={busy}
        onClick={send}
        title="Formal note to the web-found business contact asking them to route us to the right person (businesses only)"
      >
        {busy ? 'Sending…' : '📨 Send coordination note'}
      </button>
      <span className="text-[10px] text-gray-400">Businesses only — goes to the web-found contact.</span>
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}

/** Clerk view: documents a mediator explicitly shared with the court
 *  (Mo, 2026-08-31). Only court_visible case files reach this list —
 *  everything else stays mediator-private. */
function ClerkSharedFiles({ token, caseId }: { token: string; caseId: string }) {
  const [files, setFiles] = useState<CaseFile[] | null>(null);
  useEffect(() => {
    listCaseFiles(token, caseId)
      .then((res) => setFiles(res.files))
      .catch(() => setFiles([]));
  }, [token, caseId]);
  if (!files?.length) return null;
  return (
    <section className="card p-4">
      <div className="section-label">Documents from the mediator</div>
      <ul className="mt-2 space-y-1.5">
        {files.map((f) => (
          <li key={f.id} className="flex items-center gap-2 text-sm">
            <span aria-hidden>{f.content_type.startsWith('image/') ? '🖼️' : '📄'}</span>
            <button
              type="button"
              className="min-w-0 flex-1 truncate text-left text-brand hover:underline"
              title={f.file_name}
              onClick={() => {
                void fetchCaseFileBlob(token, f.id)
                  .then((blob) => {
                    const url = URL.createObjectURL(blob);
                    const win = window.open(url, '_blank');
                    if (!win) {
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = f.file_name;
                      a.click();
                    }
                    setTimeout(() => URL.revokeObjectURL(url), 60_000);
                  })
                  .catch(() => window.alert('Could not open the document.'));
              }}
            >
              {f.label || f.file_name}
            </button>
            <span className="shrink-0 text-xs text-gray-400">{formatDate(f.created_at)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Inline header editor (Danny, 2026-08-28: "make it editable from where
 *  it is — don't add a new field"). One ✎ on the case title opens case
 *  number, case name, and court date in place; the right rail stays
 *  clean. Each field saves only if changed, through the same audited
 *  endpoints as before. */
function HeaderEditRow({
  token,
  caseId,
  isCourt,
  caseNo,
  caseName,
  trialDate,
  onDone,
}: {
  token: string;
  caseId: string;
  isCourt: boolean;
  caseNo: string | null;
  caseName: string | null;
  trialDate: string | null;
  onDone: (patch: { court_case_no?: string; division?: string | null; case_name?: string | null; trial_date?: string | null } | null) => void;
}) {
  const [no, setNo] = useState(caseNo ?? '');
  const [name, setName] = useState(caseName ?? '');
  const [date, setDate] = useState(trialDate ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    const patch: { court_case_no?: string; division?: string | null; case_name?: string | null; trial_date?: string | null } = {};
    try {
      if (isCourt && no.trim() && no.trim() !== (caseNo ?? '')) {
        const res = await setCaseNumber(token, caseId, no.trim());
        patch.court_case_no = res.case_number;
        patch.division = res.division;
      }
      if (name.trim() !== (caseName ?? '')) {
        const next = name.trim() || null;
        await setCaseName(token, caseId, next);
        patch.case_name = next;
      }
      if ((date || '') !== (trialDate ?? '')) {
        await setTrialDate(token, caseId, date || null);
        patch.trial_date = date || null;
      }
      onDone(patch);
    } catch (err) {
      setError(err instanceof EngineError ? err.message : 'Could not save the changes.');
      setBusy(false);
    }
  };

  return (
    // The case name is the long field — it gets the full row at title size
    // (Danny, 2026-10-28: the inline one-row layout left ~an inch to type
    // in). Case #, date, and the buttons sit on their own line below.
    <div className="min-w-0 w-full">
      <input
        className="w-full rounded-md border border-surface-border px-3 py-1.5 text-base font-medium"
        value={name}
        placeholder="Case name (blank uses the party-derived caption)"
        aria-label="Case name"
        autoFocus
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !busy) void save(); }}
        disabled={busy}
      />
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        {isCourt && (
          <input
            className="w-28 rounded-md border border-surface-border px-2 py-1 font-mono text-sm"
            value={no}
            placeholder="Case #"
            aria-label="Case number"
            onChange={(e) => setNo(e.target.value)}
            disabled={busy}
          />
        )}
        <input
          type="date"
          className="rounded-md border border-surface-border px-2 py-1 text-sm"
          value={date}
          aria-label="Court date"
          onChange={(e) => setDate(e.target.value)}
          disabled={busy}
        />
        <button type="button" className="btn-primary !py-1 !text-xs" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn-secondary !py-1 !text-xs" disabled={busy} onClick={() => onDone(null)}>
          Cancel
        </button>
      </div>
      {error && <div className="mt-1 text-xs text-red-600">{error}</div>}
    </div>
  );
}


/** Compact per-party outreach summary, merged into the party card row
    (Rachel, 2026-09-14). Counts + expandable metadata-only attempt list —
    same data the clerk outreach panel shows. */
/** Contact extras on the party tab (Danny, 2026-09-18): comms-preference
    toggle, interpreter flag, and discreet alternate emails/phones with
    one-click make-primary (the primary is what all sends use). */
function PartyContactExtras({
  token,
  caseId,
  party,
  canMutate,
  onUpdated,
  onUnauthorized,
}: {
  token: string;
  caseId: string;
  party: Party;
  canMutate: boolean;
  onUpdated: (p: Party) => void;
  onUnauthorized: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [altValue, setAltValue] = useState('');
  const meta = (party.metadata ?? {}) as { alt_emails?: string[]; alt_phones?: string[]; interpreter_needed?: boolean };
  const altEmails = meta.alt_emails ?? [];
  const altPhones = meta.alt_phones ?? [];

  const patch = async (body: Parameters<typeof updateCaseParty>[3]) => {
    setBusy(true);
    try {
      const updated = await updateCaseParty(token, caseId, party.id, body);
      onUpdated(updated);
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      window.alert(err instanceof EngineError ? err.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  const addAlt = async () => {
    const v = altValue.trim();
    if (!v) return;
    if (v.includes('@')) await patch({ alt_emails: [...altEmails, v] });
    else await patch({ alt_phones: [...altPhones, v] });
    setAltValue('');
    setAdding(false);
  };

  if (!canMutate) return null;
  const pref = party.communication_preference ?? null;
  return (
    <div className="mt-2 space-y-1.5 text-xs">
      {altEmails.map((e) => (
        <div key={e} className="flex items-center gap-1.5 text-gray-500">
          <span className="break-all">alt: {e}</span>
          <button
            type="button"
            className="text-brand hover:underline"
            disabled={busy}
            title="Swap: this becomes the primary email all sends use"
            onClick={() => void patch({ email: e, alt_emails: [...(party.email ? [party.email] : []), ...altEmails.filter((x) => x !== e)] })}
          >
            make primary
          </button>
        </div>
      ))}
      {altPhones.map((ph) => (
        <div key={ph} className="flex items-center gap-1.5 text-gray-500">
          <span>alt: {formatPhone(ph)}</span>
          <button
            type="button"
            className="text-brand hover:underline"
            disabled={busy}
            title="Swap: this becomes the primary number all texts use"
            onClick={() => void patch({ phone: ph, alt_phones: [...(party.phone ? [party.phone] : []), ...altPhones.filter((x) => x !== ph)] })}
          >
            make primary
          </button>
        </div>
      ))}
      {adding ? (
        <div className="flex items-center gap-1.5">
          <input
            className="w-44 rounded border border-surface-border px-1.5 py-0.5 text-xs"
            placeholder="Email or phone"
            value={altValue}
            onChange={(e) => setAltValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void addAlt(); }}
          />
          <button type="button" className="text-brand hover:underline" disabled={busy} onClick={() => void addAlt()}>Add</button>
          <button type="button" className="text-gray-400 hover:text-gray-700" onClick={() => { setAdding(false); setAltValue(''); }}>✕</button>
        </div>
      ) : (
        <button type="button" className="text-gray-400 hover:text-brand" onClick={() => setAdding(true)}>+ alt email/phone…</button>
      )}
      <div className="flex items-center gap-1 pt-0.5">
        <span className="text-gray-400">Prefers:</span>
        {(
          [
            ['sms', 'SMS'],
            ['email', 'Email'],
            [null, '—'],
          ] as Array<['sms' | 'email' | null, string]>
        ).map(([value, label]) => (
          <button
            key={label}
            type="button"
            disabled={busy}
            className={`rounded px-1.5 py-0.5 ${pref === value ? 'bg-brand-subtle font-medium text-brand' : 'text-gray-500 hover:bg-surface-muted'}`}
            title={value === null ? 'Not indicated' : undefined}
            onClick={() => void patch({ communication_preference: value })}
          >
            {label}
          </button>
        ))}
      </div>
      <label className="flex items-center gap-1.5 text-gray-500">
        <input
          type="checkbox"
          checked={meta.interpreter_needed === true}
          disabled={busy}
          onChange={(e) => void patch({ interpreter_needed: e.target.checked })}
        />
        Interpreter needed
      </label>
    </div>
  );
}

/** Per-party invoicing on the tab (Danny, 2026-09-18): receives-invoice
    toggle, fees-waived toggle, this party's invoices with copyable pay
    links, inline create with an editable amount, and edit-amount via
    void-and-recreate (Stripe invoices are immutable once issued). */
function PartyInvoiceBlock({
  token,
  caseId,
  party,
  invoices,
  canMutate,
  canRefund,
  onPartyUpdated,
  onInvoicesChanged,
  onUnauthorized,
}: {
  token: string;
  caseId: string;
  party: Party;
  invoices: PartyInvoice[];
  canMutate: boolean;
  canRefund: boolean;
  onPartyUpdated: (p: Party) => void;
  onInvoicesChanged: (mut: (prev: PartyInvoice[]) => PartyInvoice[]) => void;
  onUnauthorized: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [amount, setAmount] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const meta = (party.metadata ?? {}) as { invoice_recipient?: boolean; fees_waived?: boolean };
  const receives =
    typeof meta.invoice_recipient === 'boolean'
      ? meta.invoice_recipient
      : ['claimant', 'respondent'].includes(party.role_archetype);
  const waived = meta.fees_waived === true;
  const mine = invoices.filter((i) => i.party_id === party.id);

  const patchParty = async (body: Parameters<typeof updateCaseParty>[3]) => {
    setBusy(true);
    try {
      onPartyUpdated(await updateCaseParty(token, caseId, party.id, body));
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      window.alert(err instanceof EngineError ? err.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  const create = async (cents?: number) => {
    setBusy(true);
    try {
      const inv = await createInvoice(token, caseId, { party_id: party.id, ...(cents ? { amount_cents: cents } : {}) });
      onInvoicesChanged((prev) => [...prev, inv]);
      setCreating(false);
      setAmount('');
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      window.alert(err instanceof EngineError ? err.message : 'Could not create the invoice.');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (inv: PartyInvoice) => {
    if (!window.confirm(`Cancel the ${formatCurrency(inv.amount_cents / 100)} invoice for ${party.name}? This voids it in Stripe — the party can no longer pay it.`)) return;
    setBusy(true);
    try {
      await voidInvoice(token, inv.id);
      onInvoicesChanged((prev) => prev.map((i) => (i.id === inv.id ? { ...i, status: 'VOID' } : i)));
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      window.alert(err instanceof EngineError ? err.message : 'Could not cancel the invoice.');
    } finally {
      setBusy(false);
    }
  };

  const refund = async (inv: PartyInvoice) => {
    if (!window.confirm(`Refund the ${formatCurrency(inv.amount_cents / 100)} payment to ${party.name}? This issues a full Stripe refund and cannot be undone.`)) return;
    setBusy(true);
    try {
      await refundInvoice(token, inv.id);
      onInvoicesChanged((prev) => prev.map((i) => (i.id === inv.id ? { ...i, status: 'REFUNDED', paid_at: null } : i)));
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      window.alert(err instanceof EngineError ? err.message : 'Could not refund the invoice.');
    } finally {
      setBusy(false);
    }
  };

  // Edit amount: Stripe invoices are immutable once issued, so this cancels the
  // unpaid invoice and issues a fresh one for the new amount.
  const editAmount = async (inv: PartyInvoice) => {
    const next = window.prompt(`New amount in dollars (was ${formatCurrency(inv.amount_cents / 100)}). This cancels the current invoice and issues a fresh one:`);
    if (next === null) return;
    const dollars = Number(next);
    if (!Number.isFinite(dollars) || dollars <= 0) return void window.alert('Enter a positive dollar amount.');
    setBusy(true);
    try {
      await voidInvoice(token, inv.id);
      onInvoicesChanged((prev) => prev.map((i) => (i.id === inv.id ? { ...i, status: 'VOID' } : i)));
      await create(Math.round(dollars * 100));
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      window.alert(err instanceof EngineError ? err.message : 'Could not update the amount.');
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-surface-border px-3 py-2 text-xs">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Invoicing</span>
        {canMutate && (
          <>
            <label className="flex items-center gap-1.5 text-gray-600">
              <input
                type="checkbox"
                checked={receives && !waived}
                disabled={busy || waived}
                onChange={(e) => void patchParty({ invoice_recipient: e.target.checked })}
              />
              Receives invoices
            </label>
            <label className="flex items-center gap-1.5 text-gray-600" title="Waived beats everything — this party is never auto-invoiced">
              <input
                type="checkbox"
                checked={waived}
                disabled={busy}
                onChange={(e) => void patchParty({ fees_waived: e.target.checked })}
              />
              Fees waived
            </label>
          </>
        )}
        {waived && <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-800">No fees for this party</span>}
      </div>
      {mine.length > 0 && (
        <ul className="mt-1.5 space-y-1">
          {mine.map((inv) => (
            <li key={inv.id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className={`font-medium ${inv.status === 'VOID' || inv.status === 'REFUNDED' ? 'text-gray-400' : 'text-gray-700'}`}>
                {formatCurrency(inv.amount_cents / 100)}
              </span>
              <InvoiceChip invoice={inv} />
              {inv.status === 'UNPAID' && inv.invoice_url && (
                <button
                  type="button"
                  className="text-brand hover:underline"
                  onClick={async () => {
                    try { await navigator.clipboard.writeText(inv.invoice_url!); } catch { window.prompt('Copy the pay link:', inv.invoice_url!); }
                    setCopied(inv.id);
                    setTimeout(() => setCopied((prev) => (prev === inv.id ? null : prev)), 2000);
                  }}
                >
                  {copied === inv.id ? '✓ Copied' : 'Copy pay link'}
                </button>
              )}
              {canMutate && inv.status === 'UNPAID' && (
                <>
                  <button type="button" className="text-gray-500 hover:text-gray-900" disabled={busy} onClick={() => void editAmount(inv)}>
                    Edit amount
                  </button>
                  <button type="button" className="text-red-700 hover:underline" disabled={busy} onClick={() => void cancel(inv)}>
                    Cancel
                  </button>
                </>
              )}
              {canRefund && inv.status === 'PAID' && (
                <button type="button" className="text-red-700 hover:underline" disabled={busy} onClick={() => void refund(inv)}>
                  Refund
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canMutate && !waived && (
        creating ? (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-gray-500">$</span>
            <input
              className="w-24 rounded border border-surface-border px-1.5 py-0.5 text-xs"
              placeholder="Standard"
              autoFocus
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { setCreating(false); setAmount(''); return; }
                if (e.key !== 'Enter') return;
                const dollars = amount.trim() ? Number(amount) : null;
                if (amount.trim() && (!Number.isFinite(dollars) || dollars! <= 0)) return void window.alert('Enter a positive dollar amount or leave blank.');
                void create(dollars ? Math.round(dollars * 100) : undefined);
              }}
            />
            <button
              type="button"
              className="rounded bg-brand px-2 py-0.5 text-xs font-medium text-white hover:bg-brand/90 disabled:opacity-50"
              disabled={busy}
              onClick={() => {
                const dollars = amount.trim() ? Number(amount) : null;
                if (amount.trim() && (!Number.isFinite(dollars) || dollars! <= 0)) return void window.alert('Enter a positive dollar amount or leave blank.');
                void create(dollars ? Math.round(dollars * 100) : undefined);
              }}
            >
              Send invoice
            </button>
            <button type="button" className="text-gray-400 hover:text-gray-700" onClick={() => { setCreating(false); setAmount(''); }}>✕</button>
            <span className="w-full text-[10px] text-gray-400">Leave blank for the standard fee, or enter another amount.</span>
          </div>
        ) : (
          <button type="button" className="mt-1.5 text-gray-400 hover:text-brand" onClick={() => setCreating(true)}>+ New invoice…</button>
        )
      )}
    </div>
  );
}

function AttemptsToggle({ attempts }: { attempts: PartyOutreach['attempts'] }) {
  const [open, setOpen] = useState(false);
  const fmtAt = (iso: string) =>
    new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const labels: Record<string, string> = { email: 'Email', sms: 'Text', call: 'Call' };
  return (
    <div className="mt-0.5 text-xs">
      <button type="button" className="text-brand hover:underline" onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide attempts' : `Show ${attempts.length} attempt${attempts.length === 1 ? '' : 's'}`}
      </button>
      {open && (
        <ul className="mt-1 space-y-0.5 border-t border-surface-border pt-1 text-gray-500">
          {attempts.map((a, i) => (
            <li key={i}>
              {fmtAt(a.at)} — {labels[a.channel] ?? a.channel}
              <span className="text-gray-400"> · {a.status}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

